import {
  createSignal,
  createMemo,
  createEffect,
  For,
  Show,
  ErrorBoundary,
  onMount,
  onCleanup,
  type Component,
  type JSX,
} from "solid-js";
import { Dynamic } from "solid-js/web";
import { useNavigate } from "@solidjs/router";
import { createQuery } from "@tanstack/solid-query";
import { PANEL_REGISTRY, type PanelEntry } from "./panel-registry";
import { WsProvider, useWs, jsonEq } from "./shared/ws";
import { asWsOutgoing, envelope, isWsEnvelope } from "./shared/ws-protocol";
import VideoPanel, { type ControlLaunchMode, type ControlLaunchRequest } from "./panels/video";
import RdpPanel from "./panels/rdp";
import { MobileCockpit } from "./mobile-cockpit";
import { SettingsSection } from "./settings-section";
import { CommandPalette, type CommandItem } from "./command-palette";
import { useNotify } from "./shared/notify";
import { useConfirm } from "./shared/confirm-dialog";
import { KeyboardProfileEditor } from "./shared/keyboard-profile-editor";
import { useKeyboardProfile } from "./shared/use-keyboard-profile";
import { PwaInstallButton } from "./shared/pwa-install";
import { setActivateHandler, pendingRdpTarget, setPendingRdpTarget } from "./shared/network-actions";
import { initTweaks, TweaksPanel } from "./console/tweaks";
import { TenantObjectWorkspace } from "./console/tenant-object-workspace";
import { AdminHostsView } from "./console/admin-hosts-view";
import type { HostItem } from "./console/host-helpers";
import "./app-shell.css";
import "./settings-section.css";
import "./session-cockpit.css";

interface AppProps {
  /** Callback de déconnexion (natif uniquement) — purge le token et affiche le login. */
  onSignOut?: () => void;
  /** Email de l'opérateur (pour le menu utilisateur). */
  userEmail?: string;
  /** Rôles de l'opérateur (pour filtrer les liens d'administration). */
  userRoles?: string[];
}

// ─── Sections top-level de la session ────────────────────────────────────
// Info, Prise en main, 1 tab par panel actif (top-level, sans sous-menu),
// Paramètres. La liste est dynamique selon les features de l'agent.
type SectionKind = "info" | "control" | "settings" | "panel";
export interface Section {
  id: string;
  label: string;
  kind: SectionKind;
  entry?: PanelEntry;
}

// Arbre Clients › Sites › Hosts (partagé desktop rail / mobile hub).
export interface SiteNode {
  site: string;
  siteId?: string | null;
  hosts: HostData[];
}
export interface ClientNode {
  client: string;
  clientId?: string | null;
  ini: string;
  sites: SiteNode[];
}

// Mapping facultatif de labels lisibles (sinon on tombe sur entry.title)
const PLUGIN_LABELS: Record<string, string> = {
  powershell: "Terminal",
  files: "Fichiers",
  versions: "Versions VSS",
  process: "Processus",
  autoruns: "Autoruns",
  services: "Services",
  ad: "AD",
  firewall: "Firewall",
  schtasks: "Tâches",
  choco: "Choco",
  iis: "IIS",
  docker: "Docker",
  smb: "SMB",
  exchange: "Exchange",
  hyperv: "Hyper-V",
  cluster: "Cluster",
  dns: "DNS",
  dhcp: "DHCP",
  print: "Imprimantes",
  wsus: "WSUS",
  certs: "Certificats",
  localusers: "Comptes locaux",
  hosts: "Hôtes",
  mcpSessions: "MCP",
  users: "Utilisateurs",
  aicli: "AI CLI",
  "cli-claude": "Claude",
  "cli-codex": "Codex",
  "cli-kimi": "Kimi",
  "cli-gemini": "Gemini",
  "cli-opencode": "OpenCode",
  "cli-hermes": "Hermes",
  "cli-openclaw": "OpenClaw",
  ubuntuTerminal: "Terminal Ubuntu",
  ubuntuLogs: "Logs Ubuntu",
  plugins: "Plugins",
  operators: "Opérateurs",
  handles: "Handles",
  registry: "Registre",
  software: "Logiciels",
  sql: "SQL",
  ssh: "SSH",
  winupdate: "Windows Update",
  pktmon: "Packet Mon.",
  netinspect: "HTTPS",
  lanbrowser: "LAN",
  watchguard: "WatchGuard",
  "watchguard-rules": "WG Règles",
  "watchguard-traffic": "WG Traffic",
  "watchguard-links": "WG Links",
  "watchguard-users": "WG Auth",
  "watchguard-config": "WG Config",
  "watchguard-tcpdump": "WG tcpdump",
  computeruse: "Computer Use",
  rdp: "RDP",
  policy: "Politiques",
  // ── Aperçus de panels (plugin agent à livrer) — suffixe "(aperçu)"
  //    pour signaler à l'opérateur qu'il s'agit d'une preview.
  //    eventviewer : plugin C# livré dans agent-windows-csharp/. Le panel
  //    bascule du PanelPreview à l'impl réelle si l'hôte annonce
  //    `features.eventviewer = true` (après plugin-install).
  eventviewer: "Logs",
  storage: "Disques (aperçu)",
  network: "Réseau IP (aperçu)",
  rdpsessions: "Sessions user (aperçu)",
  defender: "Defender (aperçu)",
  drivers: "Drivers (aperçu)",
  hotfix: "Hotfix (aperçu)",
};

const PANEL_PLUGIN_IDS: Record<string, string> = {
  ad: "ad",
  certs: "cert",
  choco: "choco",
  "cli-claude": "cli",
  "cli-codex": "cli",
  "cli-kimi": "cli",
  "cli-gemini": "cli",
  "cli-opencode": "cli",
  "cli-hermes": "cli",
  "cli-openclaw": "cli",
  cluster: "cluster",
  computeruse: "computeruse",
  dhcp: "dhcp",
  dns: "dns",
  docker: "docker",
  eventviewer: "eventviewer",
  exchange: "exchange",
  firewall: "firewall",
  handles: "handles",
  hyperv: "hyperv",
  iis: "iis",
  lanbrowser: "lanbrowser",
  localusers: "localusers",
  netinspect: "netinspect",
  network: "network",
  "network-wifi": "network",
  "network-lan": "network",
  "network-diag": "network",
  "network-scan": "network",
  pktmon: "pktmon",
  watchguard: "watchguard",
  "watchguard-rules": "watchguard",
  "watchguard-traffic": "watchguard",
  "watchguard-links": "watchguard",
  "watchguard-users": "watchguard",
  "watchguard-config": "watchguard",
  "watchguard-tcpdump": "watchguard",
  print: "print",
  process: "process",
  autoruns: "autoruns",
  registry: "registry",
  schtasks: "schtasks",
  services: "services",
  smb: "smb",
  software: "software",
  sql: "sql",
  ssh: "ssh",
  versions: "versions",
  winupdate: "winupdate",
  wsus: "wsus",
};

function panelPluginAvailable(entry: PanelEntry, versions: Record<string, string>): boolean {
  const pluginId = PANEL_PLUGIN_IDS[entry.id];
  return pluginId ? Boolean(versions[pluginId]) : false;
}

// ─── Cockpit : catégories d'outils (rail droit) ──────────────────────────
// Mapping panelId → catégorie, façon wireframe « Cockpit dense ». Les ids non
// listés tombent dans « Avancé » (rien n'est perdu).
interface ToolCategory {
  key: string;
  ic: string;
  n: string;
  ids: string[];
}
export const TOOL_CATEGORIES: ToolCategory[] = [
  {
    key: "systeme",
    ic: "▤",
    n: "Système",
    ids: ["process", "services", "autoruns", "eventviewer", "storage", "network", "drivers", "handles"],
  },
  {
    key: "terminal",
    ic: ">_",
    n: "Terminal",
    ids: [
      "powershell",
      "ssh",
      "ubuntuTerminal",
      "ubuntuLogs",
      "cli-claude",
      "cli-codex",
      "cli-kimi",
      "cli-gemini",
      "cli-opencode",
      "cli-hermes",
      "cli-openclaw",
      "aicli",
    ],
  },
  {
    key: "sessions",
    ic: "◉",
    n: "Sessions & accès",
    ids: ["users", "rdpsessions", "localusers", "policy", "operators"],
  },
  { key: "fichiers", ic: "▦", n: "Fichiers", ids: ["files", "versions", "registry", "software"] },
  {
    key: "reseau",
    ic: "⇄",
    n: "Réseau",
    ids: [
      "network",
      "network-wifi",
      "network-lan",
      "network-diag",
      "network-scan",
      "pktmon",
      "netinspect",
      "lanbrowser",
      "watchguard",
      "watchguard-rules",
      "watchguard-traffic",
      "watchguard-links",
      "watchguard-users",
      "watchguard-config",
      "watchguard-tcpdump",
      "dns",
      "dhcp",
    ],
  },
  { key: "securite", ic: "⛨", n: "Sécurité", ids: ["certs", "firewall", "defender", "winupdate", "wsus"] },
  {
    key: "roles",
    ic: "▣",
    n: "Rôles serveur",
    ids: ["ad", "iis", "docker", "hyperv", "sql", "exchange", "cluster", "smb", "print", "choco"],
  },
  {
    key: "avance",
    ic: "⚙",
    n: "Avancé",
    ids: ["computeruse", "rdp", "mcpSessions", "hosts", "debug", "plugins", "schtasks"],
  },
];
// Panel id → icône de catégorie (pour onglets/chips/launcher).
const PANEL_ICON: Record<string, string> = (() => {
  const m: Record<string, string> = { info: "ⓘ", control: "▶", settings: "⚙" };
  for (const c of TOOL_CATEGORIES) for (const id of c.ids) m[id] = c.ic;
  return m;
})();
// Catégorie d'un panelId (défaut « avancé »).
function categoryOfPanel(panelId: string): string {
  for (const c of TOOL_CATEGORIES) if (c.ids.includes(panelId)) return c.key;
  return "avance";
}
// Icône pour une section (info/control/settings/panel:<id>).
function iconForSection(sectionId: string): string {
  if (sectionId.startsWith("panel:")) return PANEL_ICON[sectionId.slice(6)] || "›";
  return PANEL_ICON[sectionId] || "›";
}

// ─── Cockpit : machines ouvertes (barre du haut multi-host) ──────────────
type MachineKind = "enrolled" | "agentless" | "tenant-object" | "admin-hosts";

// Un onglet « tenant-object » est un onglet de paramétrage de client ou de
// site. Contrairement aux machines enrolled/agentless, il n'a pas de session
// relay ni de WS : il sert uniquement à configurer (droits, stratégies, VOLT).
export interface TenantObjectRef {
  objectType: "client" | "site";
  objectId: string;
  objectLabel: string;
  parentLabel?: string;
}
interface AgentlessParams {
  host: string;
  port: string;
  user: string;
  protocol: string;
}
export interface OpenMachine {
  key: string; // sessionCode (enrolled) ou "agentless:<host>" (agentless)
  kind: MachineKind;
  sessionCode: string | null;
  name: string;
  sub: string; // "client › site"
  status: "on" | "idle" | "off";
  agentless?: AgentlessParams;
  tenantObject?: TenantObjectRef;
}

const OPEN_MACHINES_KEY = "rws.session.openMachines";
const ACTIVE_SECTION_PREFIX = "rws.session.active.";

function loadOpenMachines(): OpenMachine[] {
  try {
    const raw = sessionStorage.getItem(OPEN_MACHINES_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw) as unknown;
    if (Array.isArray(arr)) {
      return arr.filter((m): m is OpenMachine => !!m && typeof (m as OpenMachine).key === "string");
    }
  } catch {
    /* corrompu */
  }
  return [];
}
function saveOpenMachines(machines: OpenMachine[]) {
  try {
    sessionStorage.setItem(OPEN_MACHINES_KEY, JSON.stringify(machines));
  } catch {
    /* quota */
  }
}
function loadActiveSection(sessionCode: string | null): string | null {
  if (!sessionCode) return null;
  try {
    return sessionStorage.getItem(ACTIVE_SECTION_PREFIX + sessionCode);
  } catch {
    return null;
  }
}
function saveActiveSection(sessionCode: string | null, id: string) {
  if (!sessionCode) return;
  try {
    sessionStorage.setItem(ACTIVE_SECTION_PREFIX + sessionCode, id);
  } catch {
    /* quota */
  }
}
function clearStoredSessionUiState(scopeKey: string | null) {
  if (!scopeKey) return;
  try {
    sessionStorage.removeItem(ACTIVE_SECTION_PREFIX + scopeKey);
    sessionStorage.removeItem(VISITED_KEY_PREFIX + scopeKey);
  } catch {
    /* private mode */
  }
}

export interface HostData {
  hostId: string;
  hostname: string;
  platform: string;
  version: string;
  displayName: string | null;
  tags: string[];
  systemInfo: Record<string, unknown> | null;
  tenantId: string | null;
  clientId: string | null;
  siteId: string | null;
  clientLabel: string | null;
  siteLabel: string | null;
  riskLevel: string | null;
  domainFqdn: string | null;
  lastSeen: string;
  firstSeen: string;
  online: boolean;
  persistentSessionCode: string | null;
  vaultDefaults?: Record<string, string>;
}

// Adaptateur HostItem (console/host-helpers) → HostData (cockpit).
// Utilisé par AdminHostsView pour déléguer la connexion à connectEnrolled.
function hostItemToHostData(h: HostItem): HostData {
  return {
    hostId: h.hostId,
    hostname: h.hostname,
    platform: "",
    version: h.agentVersion ?? "",
    displayName: h.displayName ?? null,
    tags: h.tags ?? [],
    systemInfo: null,
    tenantId: null,
    clientId: null,
    siteId: null,
    clientLabel: h.clientLabel ?? null,
    siteLabel: h.siteLabel ?? null,
    riskLevel: h.riskLevel ?? null,
    domainFqdn: h.domainFqdn ?? null,
    lastSeen: h.lastSeen,
    firstSeen: "",
    online: h.status === "online" || h.status === "idle",
    persistentSessionCode: h.persistentSessionCode ?? null,
    vaultDefaults: h.vaultDefaults,
  };
}

// Persistence des onglets ouverts par session — sessionStorage pour que
// l'opérateur retrouve son contexte au reload sans polluer les autres
// sessions du navigateur.
const VISITED_KEY_PREFIX = "rws.session.visited.";

function loadVisited(sessionCode: string | null): Set<string> {
  if (!sessionCode) return new Set(["info"]);
  try {
    const raw = sessionStorage.getItem(VISITED_KEY_PREFIX + sessionCode);
    if (!raw) return new Set(["info"]);
    const arr = JSON.parse(raw) as unknown;
    if (Array.isArray(arr)) {
      const set = new Set<string>(arr.filter((x): x is string => typeof x === "string"));
      set.add("info");
      return set;
    }
  } catch {
    /* parse error — repli défaut */
  }
  return new Set(["info"]);
}

function saveVisited(sessionCode: string | null, sections: Set<string>) {
  if (!sessionCode) return;
  try {
    sessionStorage.setItem(VISITED_KEY_PREFIX + sessionCode, JSON.stringify([...sections]));
  } catch {
    /* quota / private mode — silencieux */
  }
}

export function App(props: AppProps = {}) {
  const ws = useWs();
  const navigate = useNavigate();
  const notify = useNotify();
  // initTweaks lit localStorage et applique le thème/densité au document.
  // Idempotent — safe à appeler aussi dans la console admin (déjà fait là-bas).
  initTweaks();
  const [tweaksOpen, setTweaksOpen] = createSignal(false);
  const [userMenuOpen, setUserMenuOpen] = createSignal(false);
  // Éditeur de profil clavier (modal partagé) — accessible depuis le menu
  // utilisateur en haut à droite. Le profil est partagé entre RDP et Vidéo.
  const kbProfile = useKeyboardProfile();
  const [keyboardEditorOpen, setKeyboardEditorOpen] = createSignal(false);

  // ── Pop-out mode : fenêtre séparée pour la prise en main / panel ──────────
  const urlParams = new URLSearchParams(location.search);
  const popoutPanelId = urlParams.get("popoutPanel");
  const isPopout = urlParams.get("popout") === "1" || Boolean(popoutPanelId);
  const resolvedPopoutSection = popoutPanelId || (urlParams.get("popout") === "1" ? "control" : null);

  // ── Notifications globales basées sur les transitions WS ────────────────
  // - WS reconnectée après une coupure → toast ok discret
  // - Host décroché en pleine session → toast warn (l'opérateur peut être
  //   sur un autre panel et rater l'info sinon)
  let prevWsStatus: string | null = null;
  let prevAgentOnline = false;
  let suppressPresenceToastsUntil = 0;
  const suppressPresenceToasts = () => {
    suppressPresenceToastsUntil = Date.now() + 1800;
  };
  const presenceToastsSuppressed = () => Date.now() < suppressPresenceToastsUntil;
  createEffect(() => {
    const status = ws.state.wsStatus;
    if (prevWsStatus && prevWsStatus !== "open" && status === "open") {
      notify.ok("Connexion rétablie", { durationMs: 2500 });
    }
    prevWsStatus = status;
  });
  createEffect(() => {
    const code = ws.state.sessionCode;
    const online = ws.state.nativeAgentOnline;
    if (presenceToastsSuppressed()) {
      prevAgentOnline = online;
      return;
    }
    if (code && prevAgentOnline && !online) {
      notify.warn("Hôte déconnecté", {
        hint: "L'agent natif a coupé. Reconnexion automatique dès qu'il revient.",
        durationMs: 6000,
      });
    } else if (code && !prevAgentOnline && online) {
      notify.ok("Hôte connecté", { durationMs: 2500 });
    }
    prevAgentOnline = online;
  });
  // Multi-op : peer joined / left + bascule du contrôle.
  let prevPeers: Array<{ id: string; role: string; label?: string; active?: boolean }> = [];
  createEffect(() => {
    const me = ws.state.myPeerId;
    const current = ws.state.peers.map((p) => ({ id: p.id, role: p.role, label: p.label, active: p.active }));
    if (presenceToastsSuppressed()) {
      prevPeers = current;
      return;
    }
    const prevIds = new Set(prevPeers.map((p) => p.id));
    const currentIds = new Set(current.map((p) => p.id));
    // Nouvel opérateur arrivé (autre que moi).
    for (const p of current) {
      if (p.role !== "operator" || !p.id) continue;
      if (p.id === me) continue;
      if (!prevIds.has(p.id) && prevPeers.length > 0) {
        notify.info(`${p.label || p.id.slice(0, 6)} a rejoint la session`, { durationMs: 3500 });
      }
    }
    // Opérateur parti.
    for (const p of prevPeers) {
      if (p.role !== "operator" || !p.id) continue;
      if (p.id === me) continue;
      if (!currentIds.has(p.id)) {
        notify.info(`${p.label || p.id.slice(0, 6)} a quitté la session`, { durationMs: 3500 });
      }
    }
    // Bascule du contrôle clavier+souris.
    const prevActive = prevPeers.find((p) => p.role === "operator" && p.active);
    const newActive = current.find((p) => p.role === "operator" && p.active);
    if (me && prevPeers.length > 0 && prevActive?.id !== newActive?.id) {
      if (newActive?.id === me) {
        notify.ok("Vous avez la main", { hint: "Clavier et souris vers l'hôte", durationMs: 3000 });
      } else if (prevActive?.id === me && newActive) {
        notify.warn(`${newActive.label || "Un autre opérateur"} a pris la main`, { durationMs: 4500 });
      }
    }
    prevPeers = current;
  });

  // Info par défaut à la connexion (donne immédiatement le contexte host).
  // En pop-out, on force directement le panel cible.
  const [section, setSection] = createSignal<string>(
    isPopout && resolvedPopoutSection ? resolvedPopoutSection : "info",
  );
  const [controlLaunchRequest, setControlLaunchRequest] = createSignal<ControlLaunchRequest | null>(null);
  // Command palette (Ctrl+K / Cmd+K) — items multi-catégories : onglets de
  // la session courante, hôtes cross-session (connect direct), récents.
  const [commandPaletteOpen, setCommandPaletteOpen] = createSignal(false);
  // Récents persistés en localStorage, top 5.
  const RECENTS_KEY = "rws.cmdpalette.recents";
  const MAX_RECENTS = 5;
  const [recentIds, setRecentIds] = createSignal<string[]>(
    (() => {
      try {
        const raw = localStorage.getItem(RECENTS_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw) as unknown;
        if (Array.isArray(parsed))
          return parsed.filter((x): x is string => typeof x === "string").slice(0, MAX_RECENTS);
      } catch {
        /* corrompu */
      }
      return [];
    })(),
  );
  function pushRecent(id: string) {
    setRecentIds((prev) => {
      const next = [id, ...prev.filter((x) => x !== id)].slice(0, MAX_RECENTS);
      try {
        localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
      } catch {
        /* quota */
      }
      return next;
    });
  }
  // Persistence : on garde montés tous les composants visités pour préserver
  // leur état (sessions PowerShell, tests en cours, abonnements WS). Au reload
  // on récupère les onglets précédemment ouverts via sessionStorage.
  const [visitedSections, setVisitedSectionsRaw] = createSignal<Set<string>>(
    isPopout && resolvedPopoutSection ? new Set([resolvedPopoutSection]) : loadVisited(ws.state.sessionCode),
  );

  let activeSessionCodeForUi = (): string | null => ws.state.sessionCode || null;
  let activeScopeKey = (): string | null => ws.state.sessionCode || null;
  const isActiveSessionJoined = () => {
    const code = activeSessionCodeForUi();
    return Boolean(code) && ws.state.sessionCode === code;
  };

  function setVisitedSections(updater: (prev: Set<string>) => Set<string>) {
    setVisitedSectionsRaw((prev) => {
      const next = updater(prev);
      saveVisited(activeScopeKey(), next);
      return next;
    });
  }

  // ── Focus mode (plein écran in-app) depuis le viewer vidéo ──────────────────
  const [focusMode, setFocusMode] = createSignal(false);
  function onVideoFocusMode(e: Event) {
    const detail = (e as CustomEvent).detail as { active?: boolean } | undefined;
    setFocusMode(Boolean(detail?.active));
  }
  window.addEventListener("video:focus-mode", onVideoFocusMode);
  onCleanup(() => window.removeEventListener("video:focus-mode", onVideoFocusMode));

  // Onglet par défaut/épinglé : jamais fermable (toujours un point de repli).
  const PINNED_SECTION = "info";

  function activate(id: string) {
    setVisitedSections((v) => {
      if (v.has(id)) return v;
      const n = new Set(v);
      n.add(id);
      return n;
    });
    setSection(id);
    saveActiveSection(activeScopeKey(), id);
  }
  // Expose activate() aux panels enregistrés (qui ne reçoivent pas de props) via
  // le module network-actions — utilisé par le scanner de services (network-scan)
  // pour ouvrir les panels RDP / LAN Browser depuis le panel vanilla network.
  setActivateHandler(activate);
  function openControl(mode?: ControlLaunchMode) {
    const targetSessionCode =
      activeMachine()?.kind === "enrolled"
        ? activeMachine()?.sessionCode
        : (sessionHostForPanels()?.persistentSessionCode ?? ws.state.sessionCode ?? null);
    if (mode) setControlLaunchRequest({ mode, nonce: Date.now(), sessionCode: targetSessionCode });
    activate("control");
  }

  // Section épinglée non fermable : dépend du type de machine active.
  // - enrolled/agentless : "info"
  // - tenant-object : "obj:infos"
  function pinnedSectionFor(): string {
    return isTenantObjectActive() ? "obj:infos" : PINNED_SECTION;
  }

  // Ferme explicitement un onglet : retire l'id du Set → le <Show> démonte le
  // panel et déclenche son onCleanup (libère sessions WS, abonnements, etc.).
  function closeSection(id: string) {
    const pinned = pinnedSectionFor();
    if (id === pinned) return;
    if (id === "control") setControlLaunchRequest(null);
    closePanelPopout(id);
    setVisitedSections((v) => {
      const n = new Set(v);
      n.delete(id);
      return n;
    });
    // Si l'onglet fermé était actif, basculer vers un autre onglet visité.
    if (section() === id) {
      const remaining = sectionsFromVisited(visitedSections())
        .map((s) => s.id)
        .filter((sid) => sid !== id);
      const next = remaining[remaining.length - 1] || pinned;
      setSection(next);
      saveActiveSection(activeScopeKey(), next);
    }
  }

  // ── Pop-out des panels (fenêtre opener) ───────────────────────────────────
  // Suivi des panels actuellement déportés dans une fenêtre séparée pour : (a)
  // éviter le doublon (l'onglet d'origine est masqué + rapatriable), (b) gérer
  // le retour via le bouton "Rapatrier" du popout (postMessage) ou sa fermeture.
  const [poppedOutPanels, setPoppedOutPanels] = createSignal<Set<string>>(new Set());
  const panelPopoutWins = new Map<string, Window>();
  let panelPopoutPoll: ReturnType<typeof setInterval> | null = null;

  function markPanelPoppedOut(id: string, win: Window) {
    panelPopoutWins.set(id, win);
    setPoppedOutPanels((prev) => {
      const n = new Set(prev);
      n.add(id);
      return n;
    });
    if (!panelPopoutPoll) {
      panelPopoutPoll = setInterval(() => {
        for (const [pid, w] of [...panelPopoutWins.entries()]) {
          if (!w || w.closed) clearPanelPoppedOut(pid);
        }
        if (panelPopoutWins.size === 0 && panelPopoutPoll) {
          clearInterval(panelPopoutPoll);
          panelPopoutPoll = null;
        }
      }, 800);
    }
  }
  function clearPanelPoppedOut(id: string) {
    panelPopoutWins.delete(id);
    setPoppedOutPanels((prev) => {
      if (!prev.has(id)) return prev;
      const n = new Set(prev);
      n.delete(id);
      return n;
    });
  }
  function closePanelPopout(id: string) {
    const w = panelPopoutWins.get(id);
    clearPanelPoppedOut(id);
    if (w && !w.closed) {
      try {
        w.close();
      } catch {
        /* ignore */
      }
    }
  }
  function closeAllPanelPopouts() {
    for (const id of [...panelPopoutWins.keys()]) closePanelPopout(id);
  }
  function openPanelPopout(id: string, label: string) {
    const existing = panelPopoutWins.get(id);
    if (existing && !existing.closed) {
      existing.focus();
      return;
    }
    const sc = activeSessionCodeForUi() || ws.state.sessionCode || "";
    const url = `${window.location.origin}${window.location.pathname}?popoutPanel=${encodeURIComponent(id)}${sc ? `&s=${encodeURIComponent(sc)}` : ""}`;
    const w = window.open(url, `rws-panel-${id}`, "width=1280,height=800,popup=1");
    if (!w) return;
    markPanelPoppedOut(id, w);
    void label;
  }
  function bringBackPanel(id: string) {
    closePanelPopout(id);
    activate(id);
    window.focus();
  }
  // Messages des fenêtres popout (même origine) : montage, rapatriement, fermeture.
  function onPopoutMessage(e: MessageEvent) {
    if (e.origin !== window.location.origin) return;
    const d = e.data as { type?: string; panelId?: string } | null;
    if (!d || typeof d.type !== "string" || typeof d.panelId !== "string") return;
    if (d.type === "rws:popout:bring-back") bringBackPanel(d.panelId);
    else if (d.type === "rws:popout:closed") clearPanelPoppedOut(d.panelId);
    else if (d.type === "rws:popout:mounted" && panelPopoutWins.has(d.panelId)) {
      /* déjà suivi via window.open */
    }
  }
  if (!isPopout) {
    window.addEventListener("message", onPopoutMessage);
    onCleanup(() => {
      window.removeEventListener("message", onPopoutMessage);
      if (panelPopoutPoll) clearInterval(panelPopoutPoll);
    });
  }

  // ── Côté fenêtre popout d'un panel : signaler montage/fermeture à l'opener et
  // exposer un bouton "Rapatrier" qui rend la main à la fenêtre principale. ────
  const canBringBackToOpener = () =>
    Boolean(popoutPanelId) && typeof window !== "undefined" && window.opener && !window.opener.closed;
  function bringBackToOpener() {
    try {
      window.opener?.postMessage({ type: "rws:popout:bring-back", panelId: popoutPanelId }, window.location.origin);
    } catch {
      /* ignore */
    }
    window.close();
  }
  if (isPopout && popoutPanelId) {
    onMount(() => {
      try {
        window.opener?.postMessage({ type: "rws:popout:mounted", panelId: popoutPanelId }, window.location.origin);
      } catch {
        /* ignore */
      }
    });
    const notifyClosed = () => {
      try {
        window.opener?.postMessage({ type: "rws:popout:closed", panelId: popoutPanelId }, window.location.origin);
      } catch {
        /* ignore */
      }
    };
    window.addEventListener("pagehide", notifyClosed);
    onCleanup(() => window.removeEventListener("pagehide", notifyClosed));
  }

  const sessionCode = () => ws.state.sessionCode;
  const agentOnline = () => isActiveSessionJoined() && ws.state.nativeAgentOnline;
  const features = () => (isActiveSessionJoined() ? ws.state.agentFeatures || null : null);
  const [hostPluginVersions, setHostPluginVersions] = createSignal<Record<string, string>>({});
  const pluginVersions = () => ({
    ...hostPluginVersions(),
    ...(isActiveSessionJoined() ? ws.state.pluginVersions || {} : {}),
  });

  // Liste dynamique des sections : Info / Prise en main / [panels actifs…] / Paramètres.
  //
  // IDENTITÉ STABLE : le <For> qui rend ces sections est keyé par identité
  // référentielle (SolidJS). Recréer des objets Section à contenu identique à
  // chaque recalcul détruirait/recréerait tout le sous-arbre des panels —
  // perte d'état UI et teardown des connexions vivantes (client RDP : tunnel
  // fermé `operator-disconnect`, mot de passe vidé). On réutilise donc l'objet
  // précédent tant que son contenu est inchangé : seuls les ajouts/retraits/
  // changements RÉELS produisent de nouvelles références.
  const sectionCache = new Map<string, Section>();
  const stableSection = (next: Section): Section => {
    const prev = sectionCache.get(next.id);
    // Défense en profondeur : on compare par id d'entry plutôt que par référence,
    // au cas où une future modification recréerait l'objet PanelEntry.
    if (prev && prev.label === next.label && prev.kind === next.kind && prev.entry?.id === next.entry?.id) return prev;
    sectionCache.set(next.id, next);
    return next;
  };
  const sections = createMemo<Section[]>(() => {
    const list: Section[] = [
      { id: "info", label: "Info", kind: "info" },
      { id: "control", label: "Prise en main", kind: "control" },
    ];
    const feats = features();
    for (const p of PANEL_REGISTRY) {
      if (!p.component) continue;
      const pluginAvailable = panelPluginAvailable(p, pluginVersions());
      const featureAvailable = !p.featureGate || feats?.[p.featureGate] === true;
      if (!featureAvailable && !pluginAvailable) continue;
      list.push({
        id: `panel:${p.id}`,
        label: PLUGIN_LABELS[p.id] || p.title || p.id,
        kind: "panel",
        entry: p,
      });
    }
    list.push({ id: "settings", label: "Paramètres", kind: "settings" });
    return list.map(stableSection);
  });
  function sectionFromId(id: string): Section | null {
    if (id === "info") return stableSection({ id: "info", label: "Info", kind: "info" });
    if (id === "control") return stableSection({ id: "control", label: "Prise en main", kind: "control" });
    if (id === "settings") return stableSection({ id: "settings", label: "Paramètres", kind: "settings" });
    if (!id.startsWith("panel:")) return null;
    const panelId = id.slice(6);
    const entry = PANEL_REGISTRY.find((p) => p.id === panelId && p.component);
    if (!entry) return null;
    return stableSection({
      id,
      label: PLUGIN_LABELS[entry.id] || entry.title || entry.id,
      kind: "panel",
      entry,
    });
  }
  function sectionsFromVisited(visited: Set<string>): Section[] {
    const orderedIds = [
      "info",
      "control",
      ...PANEL_REGISTRY.filter((p) => p.component).map((p) => `panel:${p.id}`),
      "settings",
    ];
    const result: Section[] = [];
    for (const id of orderedIds) {
      if (!visited.has(id)) continue;
      const s = sectionFromId(id);
      if (s) result.push(s);
    }
    return result;
  }

  // Tous les hôtes (pour la command palette cross-session). Rafraîchi
  // toutes les 30 s tant que la palette n'est pas ouverte — sinon refetch
  // immédiat à chaque ouverture.
  const allHostsQuery = createQuery<HostData[]>(() => ({
    queryKey: ["palette-hosts"],
    queryFn: async () => {
      const r = await fetch("/host/list", { credentials: "include" });
      if (!r.ok) return [];
      const data = (await r.json()) as { items?: HostData[] };
      return data.items ?? [];
    },
    refetchInterval: 30_000,
    staleTime: 10_000,
  }));
  createEffect(() => {
    if (commandPaletteOpen()) void allHostsQuery.refetch();
  });

  // Lookup du host à partir du sessionCode courant.
  const hostQuery = createQuery<HostData | null>(() => ({
    queryKey: ["session-host", sessionCode()],
    get enabled() {
      return !!sessionCode();
    },
    queryFn: async () => {
      const r = await fetch("/host/list", { credentials: "include" });
      if (!r.ok) return null;
      const data = (await r.json()) as { items?: HostData[] };
      const items = data.items || [];
      return items.find((h) => h.persistentSessionCode === sessionCode()) || null;
    },
    staleTime: 30_000,
  }));

  // ── Cockpit : machines ouvertes (barre du haut multi-host) ───────────────
  const confirm = useConfirm();
  const [openMachines, setOpenMachinesRaw] = createSignal<OpenMachine[]>(loadOpenMachines());
  const [activeMachineKey, setActiveMachineKey] = createSignal<string | null>(null);
  const dismissedSessionCodes = new Set<string>();
  // Dialogue de connexion agentless (RDP direct).
  const [agentlessOpen, setAgentlessOpen] = createSignal(false);

  function setOpenMachines(updater: (prev: OpenMachine[]) => OpenMachine[]) {
    setOpenMachinesRaw((prev) => {
      const next = updater(prev);
      saveOpenMachines(next);
      return next;
    });
  }

  const activeMachine = createMemo<OpenMachine | null>(() => {
    const key = activeMachineKey();
    return openMachines().find((m) => m.key === key) || null;
  });
  const isAgentlessActive = () => activeMachine()?.kind === "agentless";
  const isTenantObjectActive = () => activeMachine()?.kind === "tenant-object";
  const isAdminHostsActive = () => activeMachine()?.kind === "admin-hosts";
  // eslint-disable-next-line solid/reactivity -- assignation de fonction, pas un scope réactif
  activeSessionCodeForUi = () => (activeMachine()?.kind === "enrolled" ? (activeMachine()?.sessionCode ?? null) : null);
  // eslint-disable-next-line solid/reactivity -- assignation de fonction, pas un scope réactif
  activeScopeKey = () => activeMachine()?.key ?? activeSessionCodeForUi();
  const activeHostRecord = createMemo<HostData | null | undefined>(() => {
    const activeSessionCode = activeMachine()?.kind === "enrolled" ? activeMachine()?.sessionCode : null;
    const host =
      (activeSessionCode
        ? (allHostsQuery.data ?? []).find((h) => h.persistentSessionCode === activeSessionCode)
        : null) ?? hostQuery.data;
    return host;
  });
  const hostHeartbeatOnline = () => activeHostRecord()?.online === true;
  const effectiveHostOnline = () => agentOnline() || hostHeartbeatOnline();
  const sessionHostForPanels = createMemo<HostData | null | undefined>((prev) => {
    const host = activeHostRecord();
    if (!host) return host;
    const online = effectiveHostOnline();
    if (host.online === online) return host;
    const next = { ...host, online };
    if (prev && jsonEq(prev, next)) return prev;
    return next;
  });
  const enrolledMachines = createMemo(() =>
    openMachines().filter(
      (m): m is OpenMachine & { kind: "enrolled"; sessionCode: string } =>
        m.kind === "enrolled" && Boolean(m.sessionCode),
    ),
  );
  const enrolledMachineKeys = createMemo(() => enrolledMachines().map((m) => m.key));
  const agentlessMachineKeys = createMemo(() =>
    openMachines()
      .filter((m) => m.kind === "agentless")
      .map((m) => m.key),
  );
  const tenantObjectKeys = createMemo(() =>
    openMachines()
      .filter((m) => m.kind === "tenant-object")
      .map((m) => m.key),
  );
  function enrolledMachineByKey(key: string): (OpenMachine & { kind: "enrolled"; sessionCode: string }) | null {
    return (
      openMachines().find(
        (m): m is OpenMachine & { kind: "enrolled"; sessionCode: string } =>
          m.key === key && m.kind === "enrolled" && Boolean(m.sessionCode),
      ) || null
    );
  }
  function agentlessMachineByKey(key: string): (OpenMachine & { kind: "agentless" }) | null {
    return (
      openMachines().find((m): m is OpenMachine & { kind: "agentless" } => m.key === key && m.kind === "agentless") ||
      null
    );
  }
  function tenantObjectMachineByKey(
    key: string,
  ): (OpenMachine & { kind: "tenant-object"; tenantObject: TenantObjectRef }) | null {
    return (
      openMachines().find(
        (m): m is OpenMachine & { kind: "tenant-object"; tenantObject: TenantObjectRef } =>
          m.key === key && m.kind === "tenant-object" && Boolean(m.tenantObject),
      ) || null
    );
  }
  const hostForEnrolledMachineCache = new Map<string, HostData>();
  function hostForEnrolledMachine(machine: OpenMachine & { kind: "enrolled"; sessionCode: string }) {
    const host =
      (allHostsQuery.data ?? []).find((h) => h.persistentSessionCode === machine.sessionCode) ||
      (activeMachineKey() === machine.key ? hostQuery.data : null);
    if (!host) return host;
    if (activeMachineKey() !== machine.key) return host;
    const online = effectiveHostOnline();
    if (host.online === online) {
      hostForEnrolledMachineCache.set(machine.key, host);
      return host;
    }
    const next = { ...host, online };
    const cached = hostForEnrolledMachineCache.get(machine.key);
    if (cached && jsonEq(cached, next)) return cached;
    hostForEnrolledMachineCache.set(machine.key, next);
    return next;
  }
  function activeControlLaunchRequestFor(machine: OpenMachine & { kind: "enrolled"; sessionCode: string }) {
    const req = controlLaunchRequest();
    if (activeMachineKey() !== machine.key) return null;
    if (req?.sessionCode && req.sessionCode !== machine.sessionCode) return null;
    return req;
  }
  function MachineWorkspace(props: { machineKey: string }) {
    /* eslint-disable solid/reactivity */
    const machineKey = props.machineKey;
    /* eslint-enable solid/reactivity */
    const machine = () => enrolledMachineByKey(machineKey);
    const [mountedSections, setMountedSections] = createSignal<Set<string>>(loadVisited(machineKey));
    createEffect(() => {
      if (activeMachineKey() === machineKey) setMountedSections(new Set(visitedSections()));
    });
    const workspaceSections = createMemo(() => sectionsFromVisited(mountedSections()));
    return (
      <Show when={Boolean(machine())}>
        <div style={{ display: isAgentlessActive() ? "none" : "contents" }}>
          <WsProvider
            sessionCode={machine()?.sessionCode}
            closeOnCleanup
            joinLabel={activeMachineKey() === machineKey ? "Workspace actif" : "Workspace arrière-plan"}
          >
            <MachineWorkspaceContent
              machineKey={machineKey}
              sections={workspaceSections()}
              host={hostForEnrolledMachine(machine()!)}
            />
          </WsProvider>
        </div>
      </Show>
    );
  }

  function MachineWorkspaceContent(props: {
    machineKey: string;
    sections: Section[];
    host: HostData | null | undefined;
  }) {
    const scopedWs = useWs();
    const machine = () => enrolledMachineByKey(props.machineKey);
    const workspaceActive = () => activeMachineKey() === props.machineKey && !isAgentlessActive();
    const visible = (id: string) => workspaceActive() && section() === id;
    const workspaceOnline = () => scopedWs.state.nativeAgentOnline || props.host?.online === true;
    const workspaceFeatures = () => scopedWs.state.agentFeatures || (workspaceActive() ? features() : null);

    createEffect(() => {
      if (visible("panel:rdp") && pendingRdpTarget()) setPendingRdpTarget(null);
    });

    return (
      <For each={props.sections}>
        {(s) => (
          <div
            class="section-panel"
            data-section-kind={s.kind}
            data-machine-key={props.machineKey}
            style={{
              display: visible(s.id) ? "flex" : "none",
              "flex-direction": "column",
              flex: "1 1 0",
              "min-height": "0",
              height: "100%",
              width: "100%",
              position: "relative",
            }}
          >
            <Show when={poppedOutPanels().has(s.id)}>
              <div class="panel-popout-mask" role="status">
                <span>🗗 « {s.label} » est dans une fenêtre séparée</span>
                <button class="btn btn--sm" onClick={() => bringBackPanel(s.id)}>
                  ↩ Rapatrier
                </button>
              </div>
            </Show>
            <ErrorBoundary
              fallback={(err, reset) => (
                <div class="panel-crash">
                  <strong>⚠ Ce panneau a rencontré une erreur.</strong>
                  <pre class="panel-crash__msg">{String(err?.message ?? err)}</pre>
                  <button class="btn btn--sm" onClick={reset}>
                    Réessayer
                  </button>
                </div>
              )}
            >
              <Show when={s.kind === "info"}>
                <CockpitInfo
                  host={props.host}
                  loading={hostQuery.isLoading && workspaceActive()}
                  features={workspaceFeatures()}
                  sessionAgentOnline={workspaceOnline()}
                  availableTools={cockpitTools()}
                  onOpenControl={openControl}
                  onOpenTool={(id) => activate(id)}
                  onOpenSettings={() => activate("settings")}
                  onOpenAgentless={() => setAgentlessOpen(true)}
                />
              </Show>
              <Show when={s.kind === "control" && machine()}>
                <div class="cp-tool-host">
                  <VideoPanel
                    sessionHost={props.host}
                    launchRequest={activeControlLaunchRequestFor(machine()!)}
                    visible={visible(s.id)}
                  />
                </div>
              </Show>
              <Show when={s.kind === "settings"}>
                <SettingsSection host={props.host} onRefresh={() => hostQuery.refetch()} />
              </Show>
              <Show when={s.kind === "panel" && s.entry}>
                <div class="cp-tool-host">
                  <Show
                    when={s.id === "panel:rdp"}
                    fallback={
                      // vaultDefaults est consommé par les panels qui le
                      // supportent (ex. WatchGuard). Le cast évite l'erreur de
                      // typage du générique Dynamic qui ne connaît pas la prop.
                      (() => {
                        const Comp = s.entry!.component as Component<{
                          vaultDefaults?: Record<string, string>;
                        }>;
                        return <Comp vaultDefaults={props.host?.vaultDefaults ?? undefined} />;
                      })()
                    }
                  >
                    <RdpPanel
                      sessionCode={machine()?.sessionCode}
                      visible={visible(s.id)}
                      defaultHost={pendingRdpTarget()?.host}
                      defaultPort={pendingRdpTarget()?.port ? String(pendingRdpTarget()!.port!) : undefined}
                      vaultDefaults={props.host?.vaultDefaults}
                    />
                  </Show>
                </div>
              </Show>
            </ErrorBoundary>
          </div>
        )}
      </For>
    );
  }
  let loadedScopeKey: string | null | undefined;
  createEffect(() => {
    const key = activeScopeKey();
    if (key === loadedScopeKey) return;
    loadedScopeKey = key;
    if (isPopout && resolvedPopoutSection) return;
    setVisitedSectionsRaw(loadVisited(key));
    setSection(loadActiveSection(key) || PINNED_SECTION);
    setControlLaunchRequest(null);
  });
  createEffect(() => {
    const hostId = activeHostRecord()?.hostId;
    if (!hostId) {
      setHostPluginVersions({});
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const r = await fetch(`/host/${encodeURIComponent(hostId)}/plugins`, {
          credentials: "same-origin",
          headers: { accept: "application/json" },
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = (await r.json()) as {
          installed?: { id?: string; installed_version?: string | null; latest_stable?: string | null }[];
        };
        const versions: Record<string, string> = {};
        for (const plugin of data.installed || []) {
          if (plugin.id) versions[plugin.id] = plugin.installed_version || plugin.latest_stable || "installed";
        }
        if (!cancelled) setHostPluginVersions(versions);
      } catch {
        if (!cancelled) setHostPluginVersions({});
      }
    })();
    onCleanup(() => {
      cancelled = true;
    });
  });

  // Ajoute (ou met à jour) une machine et la rend active.
  function upsertMachine(machine: OpenMachine, makeActive = true) {
    setOpenMachines((prev) => {
      const idx = prev.findIndex((m) => m.key === machine.key);
      if (idx < 0) return [...prev, machine];
      const next = [...prev];
      next[idx] = { ...next[idx], ...machine };
      return next;
    });
    if (makeActive) setActiveMachineKey(machine.key);
  }

  // Bascule de machine : re-join WS pour les enrôlées, état local pour agentless.
  function switchMachine(key: string) {
    const m = openMachines().find((x) => x.key === key);
    if (!m) return;
    if (m.kind === "enrolled" && m.sessionCode) dismissedSessionCodes.delete(m.sessionCode);
    setActiveMachineKey(key);
    if (m.kind === "enrolled" && m.sessionCode && m.sessionCode !== ws.state.sessionCode) {
      suppressPresenceToasts();
      ws.send({ type: "session:join", role: "operator", sessionCode: m.sessionCode } as never);
      const wanted = loadActiveSection(m.sessionCode) || "info";
      setSection(wanted);
    }
  }

  // Connexion d'un hôte enrôlé depuis le rail Clients›Sites›Hosts.
  function connectEnrolled(h: HostData) {
    if (!h.persistentSessionCode) {
      notify.warn("Hôte non connectable", {
        hint: "Aucun code de session permanent — l'agent host doit être enrôlé.",
        durationMs: 4000,
      });
      return;
    }
    if (!h.online) {
      notify.warn("Hôte hors ligne", { durationMs: 3000 });
      return;
    }
    dismissedSessionCodes.delete(h.persistentSessionCode);
    upsertMachine({
      key: h.persistentSessionCode,
      kind: "enrolled",
      sessionCode: h.persistentSessionCode,
      name: h.displayName || h.hostname,
      sub: `${h.clientLabel || "Sans client"} › ${h.siteLabel || "—"}`,
      status: h.online ? "on" : "off",
    });
    switchMachine(h.persistentSessionCode);
    activate("info");
  }

  // Ouverture d'un onglet de paramétrage client/site (sans session relay).
  // On génère une clé stable par objet pour qu'un double-clic réutilise
  // l'onglet existant plutôt que d'en créer un doublon.
  function openTenantObject(ref: TenantObjectRef) {
    const key = `object:${ref.objectType}:${ref.objectId}`;
    const icon = ref.objectType === "client" ? "🏢" : "📍";
    upsertMachine({
      key,
      kind: "tenant-object",
      sessionCode: null,
      name: `${icon} ${ref.objectLabel}`,
      sub:
        ref.objectType === "site" && ref.parentLabel
          ? `Site · ${ref.parentLabel}`
          : ref.objectType === "client"
            ? "Client"
            : "Site",
      status: "on",
      tenantObject: ref,
    });
    switchMachine(key);
    // Les onglets tenant-object ont leur propre set de sous-sections ; on
    // force la sous-section « infos » à l'ouverture.
    setVisitedSectionsRaw(loadVisited(key));
    setSection(loadActiveSection(key) || "obj:infos");
  }

  // Ouverture de la vue RMM hosts comme onglet workspace. Contrairement aux
  // onglets machines, c'est une vue d'administration (table dense, filtres,
  // approval/refus/suppression) sans session relay. Clé stable pour qu'un
  // nouveau clic sur le logo "R" réutilise l'onglet existant.
  function openAdminHosts() {
    const key = "admin:hosts";
    const existing = openMachines().some((m) => m.key === key);
    if (!existing) {
      upsertMachine({
        key,
        kind: "admin-hosts",
        sessionCode: null,
        name: "🖥 Hosts",
        sub: "Vue d'ensemble",
        status: "on",
      });
    } else {
      switchMachine(key);
    }
  }

  // Déconnexion d'une machine (retire l'onglet ; bascule sur une autre).
  async function disconnectMachine(key: string) {
    const m = openMachines().find((x) => x.key === key);
    if (!m) return;
    const ok = await confirm.confirm({
      title: `Déconnecter ${m.name} ?`,
      body: "L'onglet machine sera fermé. La session reste disponible et reconnectable.",
      confirmLabel: "Déconnecter",
      level: "warn",
    });
    if (!ok) return;
    const closingActive = activeMachineKey() === key;
    clearStoredSessionUiState(m.key);
    if (m.kind === "enrolled" && m.sessionCode) dismissedSessionCodes.add(m.sessionCode);
    if (closingActive) {
      closeAllPanelPopouts();
      setControlLaunchRequest(null);
      setVisitedSectionsRaw(new Set([PINNED_SECTION]));
      setSection(PINNED_SECTION);
    }
    const remaining = openMachines().filter((x) => x.key !== key);
    setOpenMachines(() => remaining);
    if (closingActive) {
      const next = remaining[remaining.length - 1];
      if (next) switchMachine(next.key);
      else {
        // Plus aucun onglet ouvert : bascule sur la vue RMM hosts (landing
        // du cockpit) plutôt que de naviguer vers l'ancienne page /console.
        openAdminHosts();
      }
    }
  }

  // Fermeture d'un onglet tenant-object depuis le workspace (ex. après
  // suppression du client/site). On retrouve la clé par objectId et on
  // déconnecte l'onglet correspondant sans confirmation (déjà confirmé en amont).
  onMount(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { objectId?: string } | undefined;
      if (!detail?.objectId) return;
      const key = openMachines().find(
        (m) => m.kind === "tenant-object" && m.tenantObject?.objectId === detail.objectId,
      )?.key;
      if (key) void disconnectMachineSilent(key);
    };
    window.addEventListener("rws:tenant-object:close", handler);
    onCleanup(() => window.removeEventListener("rws:tenant-object:close", handler));
  });

  // Variante silencieuse de disconnectMachine (sans confirmation) pour les
  // fermetures programmatiques (post-suppression).
  function disconnectMachineSilent(key: string) {
    const m = openMachines().find((x) => x.key === key);
    if (!m) return;
    const closingActive = activeMachineKey() === key;
    clearStoredSessionUiState(m.key);
    if (closingActive) {
      closeAllPanelPopouts();
      setControlLaunchRequest(null);
      setVisitedSectionsRaw(new Set([pinnedSectionFor()]));
      setSection(pinnedSectionFor());
    }
    const remaining = openMachines().filter((x) => x.key !== key);
    setOpenMachines(() => remaining);
    if (closingActive) {
      const next = remaining[remaining.length - 1];
      if (next) switchMachine(next.key);
      else {
        // Plus aucun onglet ouvert : bascule sur la vue RMM hosts.
        openAdminHosts();
      }
    }
  }

  // Connexion agentless : crée un onglet machine sans agent, vue = RDP direct.
  function connectAgentless(params: AgentlessParams) {
    const host = params.host.trim();
    if (!host) return;
    const port = params.port.trim() || "3389";
    const label = `${host}:${port}`;
    const key = `agentless:${label}`;
    upsertMachine({
      key,
      kind: "agentless",
      sessionCode: null,
      name: label,
      sub: `agentless · ${params.protocol.toUpperCase()}`,
      status: "on",
      agentless: { ...params, host, port },
    });
    setActiveMachineKey(key);
    setAgentlessOpen(false);
  }

  // Connexion par code de session (dépannage à la demande / client.html) :
  // l'opérateur saisit le code d'assistance communiqué par le client, on
  // rejoint la session via WS comme une machine enrôlée. L'onglet n'est créé
  // qu'après `session:state`/`session:joined` afin de ne pas accepter
  // visuellement un code inexistant ou hors périmètre.
  function connectByCode(code: string) {
    const c = code.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
    if (!c) return;
    dismissedSessionCodes.delete(c);
    ws.send({ type: "session:join", role: "operator", sessionCode: c } as never);
    setSection("info");
    setAgentlessOpen(false);
  }

  // ── Favoris (hôtes épinglés) — partagés avec la console admin via la même
  //    clé localStorage `rws.pinnedHostIds`. ───────────────────────────────
  const PINNED_KEY = "rws.pinnedHostIds";
  function loadPinned(): string[] {
    try {
      const arr = JSON.parse(localStorage.getItem(PINNED_KEY) ?? "[]") as unknown;
      return Array.isArray(arr) ? arr.filter((x): x is string => typeof x === "string") : [];
    } catch {
      return [];
    }
  }
  const [pinnedHostIds, setPinnedHostIds] = createSignal<string[]>(loadPinned());
  function togglePin(hostId: string) {
    setPinnedHostIds((prev) => {
      const next = prev.includes(hostId) ? prev.filter((x) => x !== hostId) : [...prev, hostId];
      try {
        localStorage.setItem(PINNED_KEY, JSON.stringify(next));
      } catch {
        /* quota */
      }
      return next;
    });
  }
  // Hôtes favoris résolus depuis la liste complète (pour le rail gauche).
  const favoriteHosts = createMemo<HostData[]>(() => {
    const ids = new Set(pinnedHostIds());
    return (allHostsQuery.data ?? []).filter((h) => ids.has(h.hostId));
  });

  // Synchronise la machine active avec le sessionCode WS courant : à l'arrivée
  // d'un sessionCode (auto-join ?s= ou bascule), on s'assure qu'une machine
  // enrôlée existe et devient active (sauf si une agentless est sélectionnée).
  createEffect(() => {
    const code = ws.state.sessionCode;
    if (!code) return;
    const existing = openMachines().find((m) => m.kind === "enrolled" && m.sessionCode === code);
    if (!existing && dismissedSessionCodes.has(code)) return;
    if (!existing) {
      upsertMachine(
        {
          key: code,
          kind: "enrolled",
          sessionCode: code,
          name: code,
          sub: "—",
          status: "on",
        },
        false,
      );
    }
    // Le focus cockpit appartient à l'onglet machine parent, pas au dernier
    // `session:state` reçu. On initialise seulement quand aucun onglet n'est actif.
    if (!activeMachineKey()) setActiveMachineKey(code);
  });

  // Enrichit le libellé des machines enrôlées dès que la liste des hôtes charge.
  createEffect(() => {
    const hosts = allHostsQuery.data ?? [];
    if (hosts.length === 0) return;
    setOpenMachines((prev) =>
      prev.map((m) => {
        if (m.kind !== "enrolled" || !m.sessionCode) return m;
        const h = hosts.find((x) => x.persistentSessionCode === m.sessionCode);
        if (!h) return m;
        return {
          ...m,
          name: h.displayName || h.hostname,
          sub: `${h.clientLabel || "Sans client"} › ${h.siteLabel || "—"}`,
          status: h.online ? "on" : "off",
        };
      }),
    );
  });

  // Arbre Clients › Sites › Hosts (rail gauche) construit depuis /host/list.
  // On résout aussi les IDs (clientId/siteId) depuis les HostData pour
  // permettre l'ouverture d'un onglet de paramétrage au double-clic.
  const clientTree = createMemo<ClientNode[]>(() => {
    const hosts = allHostsQuery.data ?? [];
    // Map label client -> { id, sites: Map<label site -> {id, hosts}> }
    const byClient = new Map<
      string,
      { id: string | null; sites: Map<string, { id: string | null; hosts: HostData[] }> }
    >();
    for (const h of hosts) {
      const client = h.clientLabel || "Sans client";
      const site = h.siteLabel || "—";
      if (!byClient.has(client)) byClient.set(client, { id: null, sites: new Map() });
      const entry = byClient.get(client)!;
      if (!entry.id && h.clientId) entry.id = h.clientId;
      if (!entry.sites.has(site)) entry.sites.set(site, { id: null, hosts: [] });
      const siteEntry = entry.sites.get(site)!;
      if (!siteEntry.id && h.siteId) siteEntry.id = h.siteId;
      siteEntry.hosts.push(h);
    }
    const ini = (s: string) =>
      s
        .split(/\s+/)
        .slice(0, 2)
        .map((w) => w[0] || "")
        .join("")
        .toUpperCase() || "?";
    return [...byClient.entries()]
      .map(([client, entry]) => ({
        client,
        clientId: entry.id,
        ini: ini(client),
        sites: [...entry.sites.entries()].map(([site, se]) => ({ site, siteId: se.id, hosts: se.hosts })),
      }))
      .sort((a, b) => a.client.localeCompare(b.client));
  });
  const [openClients, setOpenClients] = createSignal<Set<string>>(new Set());
  const [openCats, setOpenCats] = createSignal<Set<string>>(new Set(["systeme", "terminal"]));
  const [leftSearch, setLeftSearch] = createSignal("");
  const [rightSearch, setRightSearch] = createSignal("");
  function toggleClient(name: string) {
    setOpenClients((s) => {
      const n = new Set(s);
      if (n.has(name)) n.delete(name);
      else n.add(name);
      return n;
    });
  }
  function toggleCat(key: string) {
    setOpenCats((s) => {
      const n = new Set(s);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });
  }

  // Outils disponibles regroupés par catégorie (rail droit), filtrés recherche.
  interface RailCat {
    key: string;
    ic: string;
    n: string;
    tools: Section[];
  }
  const railCategories = createMemo<RailCat[]>(() => {
    const q = rightSearch().trim().toLowerCase();
    const panelSections = sections().filter((s) => s.kind === "panel");
    const byCat = new Map<string, Section[]>();
    for (const s of panelSections) {
      const pid = s.entry?.id || s.id.replace(/^panel:/, "");
      const cat = categoryOfPanel(pid);
      if (!byCat.has(cat)) byCat.set(cat, []);
      byCat.get(cat)!.push(s);
    }
    return TOOL_CATEGORIES.map((c) => ({
      key: c.key,
      ic: c.ic,
      n: c.n,
      tools: (byCat.get(c.key) || []).filter((s) => !q || s.label.toLowerCase().includes(q)),
    })).filter((c) => c.tools.length > 0);
  });

  // ── Omnibox / raccourcis clavier globaux (P2.4) ──────────────────────────
  onMount(() => {
    const isTextEntry = (el: Element | null): boolean => {
      if (!el) return false;
      const tag = el.tagName;
      return tag === "INPUT" || tag === "TEXTAREA" || (el as HTMLElement).isContentEditable === true;
    };

    function onKeyDown(e: KeyboardEvent) {
      const active = document.activeElement;

      // Ctrl+K / Cmd+K → command palette (toggle). Bypass tous les inputs
      // pour rester accessible même en tapant dans un filtre.
      if ((e.ctrlKey || e.metaKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setCommandPaletteOpen((v) => !v);
        return;
      }

      // Escape : si le focus est dans un input de filtre, on le blur.
      if (e.key === "Escape") {
        if (
          active &&
          (active.classList.contains("panel-filter-input") ||
            (active.tagName === "INPUT" && (active as HTMLInputElement).type === "search"))
        ) {
          (active as HTMLElement).blur();
        }
        return;
      }

      // « / » ou Ctrl/Cmd+F → focus le 1er input de filtre du panel actif.
      const isSlash = e.key === "/" && !e.ctrlKey && !e.metaKey && !e.altKey;
      const isFind = (e.ctrlKey || e.metaKey) && (e.key === "f" || e.key === "F");
      if (!isSlash && !isFind) return;

      // Ne pas intercepter « / » quand on tape déjà dans un champ texte.
      if (isSlash && isTextEntry(active)) return;

      const panel = document.querySelector<HTMLElement>(
        '.section-panel[data-section-kind]:not([style*="display: none"])',
      );
      const scope: ParentNode = panel || document;
      const filterInput =
        scope.querySelector<HTMLInputElement>(".panel-filter-input") ||
        scope.querySelector<HTMLInputElement>('input[type="search"]');
      if (!filterInput) return;

      // preventDefault seulement si une cible existe et qu'on n'est pas déjà
      // dans un champ texte (sinon on laisse le comportement natif).
      if (!isTextEntry(active)) e.preventDefault();
      filterInput.focus();
      filterInput.select?.();
    }

    document.addEventListener("keydown", onKeyDown);
    onCleanup(() => document.removeEventListener("keydown", onKeyDown));
  });

  // Le bandeau de déconnexion s'affiche hors statut "open"/"connecting".
  const wsBannerMessage = createMemo<string | null>(() => {
    const st = ws.state.wsStatus;
    if (ws.state.lastError?.includes("fermée par un opérateur")) {
      return `⚠ ${ws.state.lastError}`;
    }
    if (st === "token_failed") return "⚠ Connexion perdue — authentification échouée, nouvelle tentative…";
    if (st === "closed" || st === "error") return "⚠ Connexion perdue — reconnexion en cours…";
    return null;
  });

  // ── État dérivé pour la barre machines ──────────────────────────────────
  const wsStatusLabel = createMemo(() => {
    const st = ws.state.wsStatus;
    if (st === "connecting") return "connexion…";
    if (st === "token_failed") return "auth échouée";
    if (st === "error") return "erreur WS";
    if (st === "closed") return "déconnecté";
    return null;
  });
  const operatorCount = () => ws.state.peers.filter((p) => p.role === "operator").length;
  const someoneElseActive = () => {
    const meId = ws.state.myPeerId;
    const active = ws.state.peers.find((p) => p.role === "operator" && p.active);
    return Boolean(meId && active && active.id !== meId);
  };

  // Sections visitées hors « info » (épinglée) — onglets d'outils de la machine.
  const toolTabSections = createMemo<Section[]>(() =>
    sectionsFromVisited(visitedSections()).filter((s) => s.id !== PINNED_SECTION),
  );

  // Outils disponibles (pour le lanceur du cockpit Info).
  const cockpitTools = createMemo(() =>
    sections()
      .filter((s) => s.kind === "panel")
      .map((s) => ({ id: s.id, label: s.label, icon: iconForSection(s.id) })),
  );

  // ── Mode popout (fenêtre détachée) : rend uniquement le panneau ciblé,
  //    sans la chrome cockpit (barre machines, rails, onglets). isPopout est
  //    constant (dérivé de l'URL au montage) → early-return sûr. ───────────
  // eslint-disable-next-line solid/components-return-once
  if (isPopout && resolvedPopoutSection) return renderPopout();

  function renderPopout() {
    const popSec = () => sections().find((s) => s.id === resolvedPopoutSection);
    return (
      <div class="cockpit cockpit--popout">
        <Show when={canBringBackToOpener()}>
          <button
            class="panel-popout-bringback btn btn--sm"
            onClick={bringBackToOpener}
            title="Rapatrier ce panneau dans la fenêtre principale"
          >
            ↩ Rapatrier
          </button>
        </Show>
        <div class="cp-tool-host">
          <ErrorBoundary
            fallback={(err, reset) => (
              <div class="panel-crash">
                <strong>⚠ Ce panneau a rencontré une erreur.</strong>
                <pre class="panel-crash__msg">{String(err?.message ?? err)}</pre>
                <button class="btn btn--sm" onClick={reset}>
                  Réessayer
                </button>
              </div>
            )}
          >
            <Show when={resolvedPopoutSection === "info"}>
              <CockpitInfo
                host={sessionHostForPanels()}
                loading={hostQuery.isLoading}
                features={features()}
                sessionAgentOnline={effectiveHostOnline()}
                availableTools={cockpitTools()}
                onOpenControl={() => {}}
                onOpenTool={() => {}}
                onOpenSettings={() => {}}
              />
            </Show>
            <Show when={resolvedPopoutSection === "control"}>
              <VideoPanel sessionHost={sessionHostForPanels()} />
            </Show>
            <Show when={resolvedPopoutSection === "settings"}>
              <SettingsSection host={sessionHostForPanels()} onRefresh={() => hostQuery.refetch()} />
            </Show>
            <Show when={popSec()?.kind === "panel" && popSec()?.entry}>
              <Dynamic component={popSec()!.entry!.component as Component} />
            </Show>
          </ErrorBoundary>
        </div>
      </div>
    );
  }

  // ── Détection mobile : petit écran + grands téléphones en paysage ───────
  // Les Pro Max récents dépassent 820px CSS en landscape ; on garde alors le
  // cockpit mobile si l'écran est tactile et peu haut, sans embarquer les iPad.
  const mobileMql =
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 820px), ((pointer: coarse) and (max-width: 1024px) and (max-height: 560px))")
      : null;
  const [isMobile, setIsMobile] = createSignal(mobileMql?.matches ?? false);
  if (mobileMql) {
    const onMql = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mobileMql.addEventListener("change", onMql);
    onCleanup(() => mobileMql.removeEventListener("change", onMql));
  }

  // Monte le panneau réel d'une section (control/panel/settings) — réutilisé
  // par le cockpit mobile pour ses panes d'outils (état préservé : un seul
  // arbre monté à la fois grâce au Show desktop/mobile exclusif).
  function renderToolContent(s: Section): JSX.Element {
    return (
      <ErrorBoundary
        fallback={(err, reset) => (
          <div class="panel-crash">
            <strong>⚠ Ce panneau a rencontré une erreur.</strong>
            <pre class="panel-crash__msg">{String(err?.message ?? err)}</pre>
            <button class="btn btn--sm" onClick={reset}>
              Réessayer
            </button>
          </div>
        )}
      >
        <Show when={s.kind === "control"}>
          <div class="cp-tool-host">
            <VideoPanel sessionHost={sessionHostForPanels()} launchRequest={controlLaunchRequest()} />
          </div>
        </Show>
        <Show when={s.kind === "settings"}>
          <SettingsSection host={sessionHostForPanels()} onRefresh={() => hostQuery.refetch()} />
        </Show>
        <Show when={s.kind === "panel" && s.entry}>
          <div class="cp-tool-host">
            <Dynamic component={s.entry!.component as Component} />
          </div>
        </Show>
      </ErrorBoundary>
    );
  }

  return (
    <Show
      when={!isMobile()}
      fallback={
        <MobileCockpit
          openMachines={openMachines()}
          favoriteHosts={favoriteHosts()}
          clientTree={clientTree()}
          pinnedHostIds={pinnedHostIds()}
          togglePin={togglePin}
          activeMachine={activeMachine()}
          connectEnrolled={connectEnrolled}
          switchMachine={switchMachine}
          disconnectMachine={disconnectMachine}
          section={section()}
          activate={activate}
          closeSection={closeSection}
          toolTabSections={toolTabSections()}
          toolCategories={railCategories()}
          sessionHost={sessionHostForPanels()}
          effectiveHostOnline={effectiveHostOnline()}
          onOpenControl={openControl}
          renderToolContent={renderToolContent}
        />
      }
    >
      <div
        class="cockpit"
        classList={{
          "cockpit--focus": focusMode(),
          "session--popout-mode": isPopout,
        }}
      >
        {/* Fenêtre popout d'un panel : bouton flottant pour rapatrier. */}
        <Show when={canBringBackToOpener()}>
          <button
            class="panel-popout-bringback btn btn--sm"
            onClick={bringBackToOpener}
            title="Rapatrier ce panneau dans la fenêtre principale"
          >
            ↩ Rapatrier
          </button>
        </Show>

        {/* Bandeau de déconnexion WebSocket */}
        <Show when={wsBannerMessage()}>
          <div class="ws-disconnect-banner" role="alert">
            <span class="ws-disconnect-banner__icon" aria-hidden="true">
              ⚠
            </span>
            <span>{wsBannerMessage()!.replace(/^⚠\s*/, "")}</span>
          </div>
        </Show>

        <Show when={tweaksOpen()}>
          <TweaksPanel onClose={() => setTweaksOpen(false)} />
        </Show>

        {/* Éditeur de profil clavier (modal partagé) — accessible depuis le menu
            utilisateur en haut à droite. Définit Cmd→Ctrl, liste noire Mac, profil
            personnalisé. Le profil est partagé entre RDP et Vidéo. */}
        <KeyboardProfileEditor
          open={keyboardEditorOpen()}
          onClose={() => setKeyboardEditorOpen(false)}
          activeProfileId={kbProfile.activeProfileId()}
          onProfileChange={kbProfile.setActiveProfileId}
        />

        {/* Dialogue de connexion agentless (RDP direct) */}
        <Show when={agentlessOpen()}>
          <AgentlessDialog
            onClose={() => setAgentlessOpen(false)}
            onConnect={connectAgentless}
            onConnectByCode={connectByCode}
          />
        </Show>

        {/* ════════ BARRE MACHINES (haut, multi-host) ════════ */}
        <div class="mbar">
          <button class="mbrand" type="button" title="Vue d'ensemble des hosts" onClick={() => openAdminHosts()}>
            <span class="g">R</span>
          </button>
          <div class="mtabs">
            <For each={openMachines()}>
              {(m) => (
                <div
                  class="mtab"
                  data-on={activeMachineKey() === m.key ? "1" : "0"}
                  data-agentless={m.kind === "agentless" ? "1" : "0"}
                  data-object={m.kind === "tenant-object" ? "1" : "0"}
                  onClick={() => switchMachine(m.key)}
                  title={`${m.name} — ${m.sub}`}
                >
                  <span class="dot" classList={{ idle: m.status === "idle", off: m.status === "off" }} />
                  <div class="txt">
                    <b>{m.name}</b>
                    <small>{m.sub}</small>
                  </div>
                  <button
                    class="mx"
                    title={`Déconnecter ${m.name}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      void disconnectMachine(m.key);
                    }}
                  >
                    ✕
                  </button>
                </div>
              )}
            </For>
            <button class="madd" onClick={() => setAgentlessOpen(true)} title="Connecter une machine sans agent (RDP)">
              + Connecter une machine
            </button>
          </div>
          <div class="mbar-right">
            {/* Multi-opérateur */}
            <Show when={operatorCount() > 1}>
              <span class="cp-chip" title="Opérateurs connectés">
                👥 {operatorCount()}
              </span>
              <Show when={someoneElseActive()}>
                <button
                  class="mbtn"
                  onClick={() => ws.send({ type: "operator:activate" } as never)}
                  title="Devenir l'opérateur actif (clavier + souris)"
                >
                  ✋ Prendre la main
                </button>
              </Show>
            </Show>
            {/* Statut WS */}
            <Show when={wsStatusLabel()} fallback={<span class="cp-chip ok">en ligne</span>}>
              <span class="cp-chip warn">{wsStatusLabel()}</span>
            </Show>
            <button class="mbtn" onClick={() => setCommandPaletteOpen(true)} title="Recherche (Ctrl+K)">
              ⌕ <span class="kbd">Ctrl K</span>
            </button>
            <button class="mbtn" onClick={() => setTweaksOpen(true)} title="Préférences (thème, densité)">
              ⚙
            </button>
            {/* Menu utilisateur : email + liens d'administration (filtrés par
                rôle) + déconnexion. Remplace l'ancien bouton "← Console". */}
            <div class="cp-user-menu">
              <button
                class="mbtn cp-user-btn"
                type="button"
                onClick={() => setUserMenuOpen((v) => !v)}
                title={props.userEmail ?? "Utilisateur"}
              >
                <span class="cp-user-avatar">{(props.userEmail ?? "?").slice(0, 1).toUpperCase()}</span>
                <span class="cp-user-name">{props.userEmail ?? "utilisateur"}</span>
                <span class="cp-user-chevron" aria-hidden="true">
                  ▼
                </span>
              </button>
              <Show when={userMenuOpen()}>
                <div class="cp-user-overlay" onClick={() => setUserMenuOpen(false)} />
                <div class="cp-user-dropdown">
                  <div class="cp-user-dropdown-email">{props.userEmail ?? "utilisateur"}</div>
                  <Show
                    when={
                      props.userRoles?.includes("admin") ||
                      props.userRoles?.includes("super_admin") ||
                      props.userRoles?.includes("support") ||
                      props.userRoles?.includes("auditor")
                    }
                  >
                    <div class="cp-user-dropdown-divider" />
                    <div class="cp-user-dropdown-section">Administration</div>
                    <button
                      class="cp-user-dropdown-item"
                      type="button"
                      onClick={() => {
                        setUserMenuOpen(false);
                        navigate("/console/clients");
                      }}
                    >
                      🏢 Clients &amp; sites
                    </button>
                    <button
                      class="cp-user-dropdown-item"
                      type="button"
                      onClick={() => {
                        setUserMenuOpen(false);
                        navigate("/console/sessions");
                      }}
                    >
                      📺 Sessions
                    </button>
                    <button
                      class="cp-user-dropdown-item"
                      type="button"
                      onClick={() => {
                        setUserMenuOpen(false);
                        navigate("/console/audit");
                      }}
                    >
                      📋 Audit
                    </button>
                    <button
                      class="cp-user-dropdown-item"
                      type="button"
                      onClick={() => {
                        setUserMenuOpen(false);
                        navigate("/console/settings");
                      }}
                    >
                      ⚙ Réglages
                    </button>
                    <Show when={props.userRoles?.includes("super_admin")}>
                      <button
                        class="cp-user-dropdown-item cp-user-dropdown-item--admin"
                        type="button"
                        onClick={() => {
                          setUserMenuOpen(false);
                          navigate("/console/super-admin");
                        }}
                      >
                        ⭐ Super Admin
                      </button>
                    </Show>
                  </Show>
                  <div class="cp-user-dropdown-divider" />
                  <button
                    class="cp-user-dropdown-item"
                    type="button"
                    onClick={() => {
                      setUserMenuOpen(false);
                      setKeyboardEditorOpen(true);
                    }}
                  >
                    ⌨ Préférences clavier
                  </button>
                  <button
                    class="cp-user-dropdown-item"
                    type="button"
                    onClick={() => {
                      setUserMenuOpen(false);
                      setTweaksOpen(true);
                    }}
                  >
                    ⚙ Tweaks
                  </button>
                  <Show when={props.onSignOut}>
                    <button
                      class="cp-user-dropdown-item cp-user-dropdown-danger"
                      type="button"
                      onClick={() => {
                        setUserMenuOpen(false);
                        props.onSignOut?.();
                      }}
                    >
                      Se déconnecter
                    </button>
                  </Show>
                  <Show when={!props.onSignOut}>
                    <button
                      class="cp-user-dropdown-item cp-user-dropdown-danger"
                      type="button"
                      onClick={async () => {
                        setUserMenuOpen(false);
                        try {
                          await fetch("/api/auth/sign-out", { credentials: "include" });
                        } catch {
                          /* ignore */
                        }
                        window.location.href = "/login";
                      }}
                    >
                      Se déconnecter
                    </button>
                  </Show>
                </div>
              </Show>
            </div>
            <PwaInstallButton />
          </div>
        </div>

        {/* ════════ ONGLETS OUTILS (2e rangée, par machine) ════════ */}
        {/* Masqué pour admin-hosts : la vue RMM occupe toute la largeur. */}
        <Show when={!isAdminHostsActive()}>
          <Show
            when={isAgentlessActive()}
            fallback={
              <Show
                when={!isTenantObjectActive()}
                fallback={
                  // Onglets dédiés au paramétrage d'un client ou d'un site :
                  // pas d'outils agent, juste 4 sous-sections de configuration.
                  <div class="ttabs">
                    <For
                      each={[
                        { id: "obj:infos", icon: "ⓘ", label: "Infos", pinned: true },
                        { id: "obj:rbac", icon: "🔐", label: "Droits & Stratégies" },
                        { id: "obj:volt", icon: "🔑", label: "VOLT" },
                        { id: "obj:hosts", icon: "🖥", label: "Hôtes" },
                      ]}
                    >
                      {(t) => (
                        <div class="ttab" data-on={section() === t.id ? "1" : "0"} onClick={() => activate(t.id)}>
                          <span class="ti">{t.icon}</span> {t.label}
                          {t.pinned ? <span class="pin">épinglé</span> : null}
                          <Show when={!t.pinned}>
                            <button
                              class="tc"
                              title={`Fermer ${t.label}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                closeSection(t.id);
                              }}
                            >
                              ✕
                            </button>
                          </Show>
                        </div>
                      )}
                    </For>
                  </div>
                }
              >
                <div class="ttabs">
                  {/* Info — épinglée */}
                  <div class="ttab" data-on={section() === "info" ? "1" : "0"} onClick={() => activate("info")}>
                    <span class="ti">ⓘ</span> Info <span class="pin">épinglé</span>
                  </div>
                  <For each={toolTabSections()}>
                    {(s) => (
                      <div
                        class="ttab"
                        data-on={section() === s.id ? "1" : "0"}
                        onClick={() => activate(s.id)}
                        title={s.entry?.title || s.label}
                      >
                        <span class="ti">{iconForSection(s.id)}</span> {s.label}
                        {/* Popout */}
                        <span
                          class="tc"
                          role="button"
                          title={poppedOutPanels().has(s.id) ? `Rapatrier ${s.label}` : `Fenêtre séparée`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (poppedOutPanels().has(s.id)) bringBackPanel(s.id);
                            else openPanelPopout(s.id, s.label);
                          }}
                        >
                          {poppedOutPanels().has(s.id) ? "↩" : "🗗"}
                        </span>
                        <button
                          class="tc"
                          title={`Fermer ${s.label}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            closeSection(s.id);
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    )}
                  </For>
                  <button class="tadd" title="Ouvrir un outil (Ctrl+K)" onClick={() => setCommandPaletteOpen(true)}>
                    ＋
                  </button>
                </div>
              </Show>
            }
          >
            <div class="ttabs">
              <div class="ttab" data-on="1">
                <span class="ti">🖥</span> Bureau {activeMachine()?.agentless?.protocol?.toUpperCase() || "RDP"}
                <span class="pin">agentless</span>
              </div>
            </div>
          </Show>
        </Show>

        {/* ════════ CORPS : rails + centre ════════ */}
        <div class="cp-body">
          {/* ── Rail gauche : Clients › Sites › Hosts (masqué pour admin-hosts) ── */}
          <Show when={!isAdminHostsActive()}>
            <aside class="rail rail-l">
              <div class="rail-edge" />
              <div class="rail-vlabel">Clients</div>
              <div class="rail-head">
                <span class="h">⌂</span>
                <span>Clients &amp; sites</span>
              </div>
              <div class="rail-search sk2">
                <span>⌕</span>
                <input
                  placeholder="Rechercher un host…"
                  value={leftSearch()}
                  onInput={(e) => setLeftSearch(e.currentTarget.value)}
                />
              </div>
              <div class="rail-scroll">
                {/* ── Favoris (hôtes épinglés) ── */}
                <Show when={favoriteHosts().length > 0}>
                  <div class="cat" data-open="1">
                    <div class="catrow">
                      <span class="ci">★</span>
                      <span class="cn">Favoris</span>
                      <span class="cv">{favoriteHosts().length}</span>
                    </div>
                    <div class="ctools">
                      <For each={favoriteHosts()}>
                        {(h) => {
                          const connected = () =>
                            openMachines().some(
                              (m) => m.kind === "enrolled" && m.sessionCode === h.persistentSessionCode,
                            );
                          return (
                            <div
                              class="ctool host"
                              classList={{ conn: connected(), offline: !h.online }}
                              data-on={activeMachine()?.sessionCode === h.persistentSessionCode ? "1" : "0"}
                              title={`${h.displayName || h.hostname} · ${h.online ? "en ligne" : "hors ligne"}`}
                              onClick={() => connectEnrolled(h)}
                            >
                              <span class="dot" classList={{ off: !h.online }} />
                              <span class="host-name">{h.displayName || h.hostname}</span>
                              <span
                                class="pin-star on"
                                title="Retirer des favoris"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  togglePin(h.hostId);
                                }}
                              >
                                ★
                              </span>
                            </div>
                          );
                        }}
                      </For>
                    </div>
                  </div>
                </Show>
                <Show
                  when={clientTree().length > 0}
                  fallback={<div class="rail-empty">Aucun hôte enrôlé visible.</div>}
                >
                  <For each={clientTree()}>
                    {(c) => {
                      const q = () => leftSearch().trim().toLowerCase();
                      const sitesFiltered = () =>
                        c.sites
                          .map((s) => ({
                            site: s.site,
                            siteId: s.siteId ?? null,
                            hosts: s.hosts.filter(
                              (h) => !q() || (h.displayName || h.hostname).toLowerCase().includes(q()),
                            ),
                          }))
                          .filter((s) => s.hosts.length > 0);
                      const isOpen = () => openClients().has(c.client) || q().length > 0;
                      return (
                        <Show when={sitesFiltered().length > 0}>
                          <div class="cat" data-open={isOpen() ? "1" : "0"}>
                            <div
                              class="catrow"
                              onClick={() => toggleClient(c.client)}
                              onDblClick={(e) => {
                                e.stopPropagation();
                                if (c.clientId) {
                                  openTenantObject({
                                    objectType: "client",
                                    objectId: c.clientId,
                                    objectLabel: c.client,
                                  });
                                } else {
                                  notify.info("Client non identifiable", {
                                    hint: "Aucun hôte de ce client n'a de clientId résolu.",
                                    durationMs: 4000,
                                  });
                                }
                              }}
                              title={`${c.client} — double-clic pour paramétrer`}
                            >
                              <span class="ci cl">{c.ini}</span>
                              <span class="cn">{c.client}</span>
                              <span class="cv">{isOpen() ? "▾" : "▸"}</span>
                            </div>
                            <div class="ctools">
                              <For each={sitesFiltered()}>
                                {(s) => (
                                  <>
                                    <div
                                      class="site-lbl"
                                      onDblClick={(e) => {
                                        e.stopPropagation();
                                        if (s.siteId) {
                                          openTenantObject({
                                            objectType: "site",
                                            objectId: s.siteId,
                                            objectLabel: s.site,
                                            parentLabel: c.client,
                                          });
                                        } else {
                                          notify.info("Site non identifiable", {
                                            hint: "Aucun hôte de ce site n'a de siteId résolu.",
                                            durationMs: 4000,
                                          });
                                        }
                                      }}
                                      title={`${s.site} — double-clic pour paramétrer`}
                                    >
                                      {s.site}
                                    </div>
                                    <For each={s.hosts}>
                                      {(h) => {
                                        const connected = () =>
                                          openMachines().some(
                                            (m) => m.kind === "enrolled" && m.sessionCode === h.persistentSessionCode,
                                          );
                                        return (
                                          <div
                                            class="ctool host"
                                            classList={{ conn: connected(), offline: !h.online }}
                                            data-on={
                                              activeMachine()?.sessionCode === h.persistentSessionCode ? "1" : "0"
                                            }
                                            title={`${h.displayName || h.hostname} · ${h.online ? "en ligne" : "hors ligne"}`}
                                            onClick={() => connectEnrolled(h)}
                                          >
                                            <span class="dot" classList={{ idle: false, off: !h.online }} />
                                            <span class="host-name">{h.displayName || h.hostname}</span>
                                            <span
                                              class="pin-star"
                                              classList={{ on: pinnedHostIds().includes(h.hostId) }}
                                              title={
                                                pinnedHostIds().includes(h.hostId)
                                                  ? "Retirer des favoris"
                                                  : "Ajouter aux favoris"
                                              }
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                togglePin(h.hostId);
                                              }}
                                            >
                                              {pinnedHostIds().includes(h.hostId) ? "★" : "☆"}
                                            </span>
                                          </div>
                                        );
                                      }}
                                    </For>
                                  </>
                                )}
                              </For>
                            </div>
                          </div>
                        </Show>
                      );
                    }}
                  </For>
                </Show>
              </div>
            </aside>
          </Show>

          {/* ── Rail droit : Outils catégorisés (masqué pour agentless, tenant-object et admin-hosts) ── */}
          <Show when={!isAgentlessActive() && !isTenantObjectActive() && !isAdminHostsActive()}>
            <aside class="rail rail-r">
              <div class="rail-edge" />
              <div class="rail-vlabel">Outils</div>
              <div class="rail-head">
                <span class="h">☰</span>
                <span>Outils</span>
              </div>
              <div class="rail-search sk2">
                <span>⌕</span>
                <input
                  placeholder="Rechercher un outil…"
                  value={rightSearch()}
                  onInput={(e) => setRightSearch(e.currentTarget.value)}
                />
              </div>
              <div class="rail-scroll">
                <Show
                  when={railCategories().length > 0}
                  fallback={<div class="rail-empty">Aucun outil disponible sur cet agent.</div>}
                >
                  <For each={railCategories()}>
                    {(c) => {
                      const isOpen = () => openCats().has(c.key) || rightSearch().trim().length > 0;
                      return (
                        <div class="cat" data-open={isOpen() ? "1" : "0"}>
                          <div class="catrow" onClick={() => toggleCat(c.key)}>
                            <span class="ci">{c.ic}</span>
                            <span class="cn">{c.n}</span>
                            <span class="cv">
                              {c.tools.length} {isOpen() ? "▾" : "▸"}
                            </span>
                          </div>
                          <div class="ctools">
                            <For each={c.tools}>
                              {(t) => (
                                <div
                                  class="ctool"
                                  data-on={section() === t.id ? "1" : "0"}
                                  title={t.entry?.title || t.label}
                                  onClick={() => activate(t.id)}
                                >
                                  {t.label}
                                </div>
                              )}
                            </For>
                          </div>
                        </div>
                      );
                    }}
                  </For>
                </Show>
              </div>
            </aside>
          </Show>

          {/* ── Centre ── */}
          <main class="cp-main">
            {/* Workspaces par machine enrôlée : chaque host ouvert garde son
              propre arbre de panels et sa propre WS opérateur. Changer
              d'onglet host masque seulement l'ancien workspace ; fermer
              l'onglet host démonte le workspace et ferme ses connexions. */}
            <For each={enrolledMachineKeys()}>{(machineKey) => <MachineWorkspace machineKey={machineKey} />}</For>

            {/* Machines agentless — chaque RdpPanel reste monté (préserve sa
              connexion), affiché selon la machine active. */}
            <For each={agentlessMachineKeys()}>
              {(machineKey) => {
                const machine = () => agentlessMachineByKey(machineKey);
                return (
                  <div
                    class="section-panel"
                    style={{
                      display: activeMachineKey() === machineKey ? "flex" : "none",
                      "flex-direction": "column",
                      flex: "1 1 0",
                      "min-height": "0",
                      height: "100%",
                      width: "100%",
                    }}
                  >
                    <div class="cp-tool-host">
                      <ErrorBoundary
                        fallback={(err) => (
                          <div class="panel-crash">
                            <strong>⚠ Connexion agentless impossible.</strong>
                            <pre class="panel-crash__msg">{String(err?.message ?? err)}</pre>
                          </div>
                        )}
                      >
                        <RdpPanel defaultHost={machine()?.agentless?.host} defaultPort={machine()?.agentless?.port} />
                      </ErrorBoundary>
                    </div>
                  </div>
                );
              }}
            </For>

            {/* Onglets de paramétrage client/site (tenant-object) — un
              workspace par objet ouvert, affiché selon l'onglet actif. Pas de
              WS : ces workspaces utilisent uniquement des appels REST. */}
            <For each={tenantObjectKeys()}>
              {(machineKey) => {
                const machine = () => tenantObjectMachineByKey(machineKey);
                const active = () => activeMachineKey() === machineKey;
                return (
                  <div
                    class="section-panel tenant-object-panel"
                    data-section-kind="tenant-object"
                    style={{
                      display: active() ? "flex" : "none",
                      "flex-direction": "column",
                      flex: "1 1 0",
                      "min-height": "0",
                      height: "100%",
                      width: "100%",
                      position: "relative",
                      overflow: "auto",
                    }}
                  >
                    <ErrorBoundary
                      fallback={(err, reset) => (
                        <div class="panel-crash">
                          <strong>⚠ Erreur de paramétrage.</strong>
                          <pre class="panel-crash__msg">{String(err?.message ?? err)}</pre>
                          <button class="btn btn--sm" onClick={reset}>
                            Réessayer
                          </button>
                        </div>
                      )}
                    >
                      <Show when={machine()}>
                        <TenantObjectWorkspace
                          ref={machine()!.tenantObject}
                          activeSection={section()}
                          hosts={(allHostsQuery.data ?? []).filter((h) => {
                            const obj = machine()!.tenantObject;
                            if (obj.objectType === "client") {
                              return (h.clientId ?? null) === obj.objectId || h.clientLabel === obj.objectLabel;
                            }
                            return (h.siteId ?? null) === obj.objectId || h.siteLabel === obj.objectLabel;
                          })}
                          onConnectHost={(h) => connectEnrolled(h)}
                        />
                      </Show>
                    </ErrorBoundary>
                  </div>
                );
              }}
            </For>

            {/* Vue RMM hosts (admin-hosts) — table dense plein écran, sans
              rails ni onglets outils. L'ouverture d'un host délègue à
              connectEnrolled via l'adaptateur hostItemToHostData. */}
            <Show when={isAdminHostsActive()}>
              <div
                class="section-panel admin-hosts-panel"
                style={{
                  display: "flex",
                  "flex-direction": "column",
                  flex: "1 1 0",
                  "min-height": "0",
                  height: "100%",
                  width: "100%",
                  overflow: "auto",
                }}
              >
                <AdminHostsView onConnectEnrolled={(h) => connectEnrolled(hostItemToHostData(h))} />
              </div>
            </Show>

            {/* État vide : aucune machine */}
            <Show when={openMachines().length === 0}>
              <div class="rail-empty" style={{ margin: "auto", "text-align": "center", "max-width": "320px" }}>
                Aucune machine connectée. Ouvre le rail gauche (Clients &amp; sites) pour prendre la main sur un hôte,
                ou « + Connecter une machine » pour une connexion agentless.
              </div>
            </Show>
          </main>
        </div>

        {/* Command palette — Ctrl+K / Cmd+K */}
        <CommandPalette
          open={commandPaletteOpen}
          onClose={() => setCommandPaletteOpen(false)}
          onUsed={(it) => pushRecent(it.id)}
          items={() => {
            const built: CommandItem[] = [];
            // Onglets de la session courante.
            const tabItems: CommandItem[] = sections().map((s) => ({
              id: `tab:${s.id}`,
              label: s.label,
              hint:
                s.kind === "info"
                  ? "Onglet · Vue d'ensemble"
                  : s.kind === "control"
                    ? "Onglet · Prise en main"
                    : s.kind === "settings"
                      ? "Onglet · Paramètres"
                      : "Panel" + (s.entry?.id ? ` · ${s.entry.id}` : ""),
              category: "Onglets",
              current: s.id === section(),
              onSelect: () => activate(s.id),
            }));
            // Hôtes cross-session — envoie session:join via WS, le shell réagit
            // au changement de sessionCode et recharge l'état (visitedSections,
            // hostQuery, etc.).
            const hostItems: CommandItem[] = (allHostsQuery.data ?? [])
              .filter((h) => h.persistentSessionCode)
              .map((h) => ({
                id: `host:${h.hostId}`,
                label: h.displayName || h.hostname,
                hint: `Hôte · ${h.platform || "?"} · ${h.online ? "en ligne" : "hors ligne"}`,
                category: "Hôtes",
                disabled: !h.online,
                current: h.persistentSessionCode === sessionCode(),
                onSelect: () => connectEnrolled(h),
              }));

            // Récents : projection des id stockés vers leurs items définis.
            // Si un récent référence un onglet/hôte qui n'existe plus, on l'omet.
            const all = [...tabItems, ...hostItems];
            const byId = new Map(all.map((it) => [it.id, it]));
            const recentItems: CommandItem[] = recentIds()
              .map((id) => byId.get(id))
              .filter((it): it is CommandItem => it != null && !it.current)
              .map((it) => ({ ...it, category: "Récents" }));

            built.push(...recentItems, ...tabItems, ...hostItems);
            return built;
          }}
        />
      </div>
    </Show>
  );
}

// ─── COCKPIT INFO — page Info « cockpit dense » (wireframe D) ─────────────
// Aperçu gaté par consentement + actions rapides + lanceur + télémétrie
// (identité, santé live, sessions). Réutilise InfoSection pour les détails.
export interface CockpitTool {
  id: string;
  label: string;
  icon: string;
}
function CockpitInfo(props: {
  host: HostData | null | undefined;
  loading: boolean;
  features: Record<string, boolean> | null;
  sessionAgentOnline?: boolean;
  availableTools: CockpitTool[];
  onOpenControl: (mode?: ControlLaunchMode) => void;
  onOpenTool: (id: string) => void;
  onOpenSettings: () => void;
  onOpenAgentless?: () => void;
}) {
  const ws = useWs();
  const notify = useNotify();
  const confirm = useConfirm();
  const [liveData, setLiveData] = createSignal<Record<string, unknown>>({});
  const [showDetails, setShowDetails] = createSignal(false);
  const [launchQuery, setLaunchQuery] = createSignal("");

  // ── Shadow RDP par session (Lot F) ──────────────────────────────────────
  // Bouton « RDP » d'une ligne de session : on envoie `rds:shadow_start`
  // {targetSessionId}. Le host ouvre un tunnel RDP (shadow si verrouillé,
  // classique sinon) et répond `rds:shadow_result {ok,port,mode,shadowId}`.
  // On monte alors un RdpPanel pointant 127.0.0.1:port. Sur bascule
  // verrouillage/déverrouillage, le host pousse `rds:shadow_mode {mode,port}`
  // → on repointe le tunnel sur le nouveau port (le RdpPanel applique le
  // nouveau defaultPort via son createEffect interne) et on met à jour le
  // badge de mode.
  const [shadowOpen, setShadowOpen] = createSignal(false);
  const [shadowPort, setShadowPort] = createSignal<number | null>(null);
  const [shadowMode, setShadowMode] = createSignal<string>("");
  const [shadowId, setShadowId] = createSignal<string | null>(null);
  const [shadowTarget, setShadowTarget] = createSignal<number | null>(null);
  const [shadowPending, setShadowPending] = createSignal(false);

  // Abonnement live sysinfo (CPU/RAM/disque/réseau) pour les jauges santé.
  onMount(() => {
    let subscribed = false;
    const subTimer = setInterval(() => {
      if (!ws.isOpen() || !ws.state.sessionCode || subscribed) return;
      try {
        ws.send(asWsOutgoing(envelope("live", "panel", "subscribe", { topic: "sysinfo", interval: 5000 })));
        subscribed = true;
      } catch {
        /* noop */
      }
    }, 1000);
    const off = ws.on(
      (m) =>
        isWsEnvelope(m, "live", "panel", "data") &&
        (m as { payload?: { topic?: string } }).payload?.topic === "sysinfo",
      (msg) => {
        const data = (msg as { payload?: { data?: Record<string, unknown> } }).payload?.data || {};
        setLiveData(data);
      },
    );
    onCleanup(() => {
      clearInterval(subTimer);
      if (subscribed && ws.isOpen()) {
        try {
          ws.send(asWsOutgoing(envelope("live", "panel", "unsubscribe", { topic: "sysinfo" })));
        } catch {
          /* noop */
        }
      }
      off();
    });
  });

  // Listeners shadow RDP (Lot F) — `rds:shadow_result` ouvre le panneau RDP
  // sur le port retourné ; `rds:shadow_mode` repointe le tunnel à chaud.
  onMount(() => {
    const offResult = ws.on(
      (m) => (m as { type?: string }).type === "rds:shadow_result",
      (m) => {
        const r = m as {
          type: "rds:shadow_result";
          ok: boolean;
          port?: number;
          mode?: string;
          shadowId?: string;
          error?: string;
        };
        setShadowPending(false);
        if (r.ok && typeof r.port === "number") {
          setShadowPort(r.port);
          setShadowMode(r.mode || "");
          setShadowId(r.shadowId || null);
          setShadowOpen(true);
        } else {
          notify.warn(`RDP indisponible — ${r.error || "erreur inconnue"}`, { durationMs: 4000 });
        }
      },
    );
    const offMode = ws.on(
      (m) => (m as { type?: string }).type === "rds:shadow_mode",
      // eslint-disable-next-line solid/reactivity -- handler ws.on
      (m) => {
        const r = m as { type: "rds:shadow_mode"; mode?: string; port?: number; shadowId?: string };
        // N'appliquer que si ça concerne le tunnel courant (ou non identifié).
        if (r.shadowId && shadowId() && r.shadowId !== shadowId()) return;
        if (typeof r.port === "number") setShadowPort(r.port);
        if (r.mode) setShadowMode(r.mode);
      },
    );
    onCleanup(() => {
      offResult();
      offMode();
    });
  });

  function startShadowRdp(sessionId: number) {
    if (!online()) return;
    setShadowTarget(sessionId);
    setShadowPending(true);
    ws.send({
      type: "rds:shadow_start",
      requestId: crypto.randomUUID(),
      targetSessionId: sessionId,
      mode: "shadow",
    } as never);
  }

  function closeShadowRdp() {
    const id = shadowId();
    if (id) ws.send({ type: "rds:shadow_stop", shadowId: id } as never);
    setShadowOpen(false);
    setShadowPort(null);
    setShadowMode("");
    setShadowId(null);
    setShadowTarget(null);
  }

  // Consentement → état d'aperçu (granted / pending / refused).
  const consent = createMemo<"granted" | "pending" | "refused">(() => {
    const view = ws.state.access?.view;
    if (ws.state.supportApproved || view === "approved" || view === "not_required") return "granted";
    if (view === "denied") return "refused";
    return "pending";
  });

  const sysStatic = () => (props.host?.systemInfo || {}) as Record<string, unknown>;
  function num(...keys: string[]): number | null {
    const l = pickNum(liveData(), keys);
    if (l !== null) return l;
    return pickNum(sysStatic(), keys);
  }
  function str(...keys: string[]): string | null {
    for (const src of [liveData(), sysStatic()]) {
      for (const k of keys) {
        const v = src[k];
        if (v !== undefined && v !== null && v !== "") return String(v);
      }
    }
    return null;
  }

  // Jauges santé — best-effort selon les clés disponibles côté agent.
  interface Gauge {
    l: string;
    v: string;
    p: number;
  }
  const gauges = createMemo<Gauge[]>(() => {
    const cpu = num("cpuPercent", "cpu", "cpuUsage", "cpuLoadPercent");
    const mem = num("memPercent", "memoryPercent", "memoryLoadPercent", "ramPercent");
    const memUsed = num("memoryUsedGb", "ramUsedGb");
    const memTot = num("memoryGb", "memoryTotalGb", "ramGb");
    const disk = num("diskPercent", "diskCPercent", "systemDrivePercent");
    const diskUsed = num("diskUsedGb", "systemDriveUsedGb");
    const diskTot = num("diskTotalGb", "systemDriveTotalGb");
    const down = num("netDownMbps", "rxMbps", "downloadMbps");
    const up = num("netUpMbps", "txMbps", "uploadMbps");
    return [
      { l: "CPU", v: cpu !== null ? `${Math.round(cpu)} %` : "—", p: cpu ?? 0 },
      {
        l: "RAM",
        v:
          memUsed !== null && memTot !== null
            ? `${memUsed} / ${memTot} Go`
            : mem !== null
              ? `${Math.round(mem)} %`
              : "—",
        p: mem ?? (memUsed !== null && memTot ? (memUsed / memTot) * 100 : 0),
      },
      {
        l: "Disque C:",
        v:
          diskUsed !== null && diskTot !== null
            ? `${diskUsed} / ${diskTot} Go`
            : disk !== null
              ? `${Math.round(disk)} %`
              : "—",
        p: disk ?? 0,
      },
      {
        l: "Réseau",
        v: down !== null || up !== null ? `↓${down ?? 0} ↑${up ?? 0} Mb/s` : "—",
        p: Math.min(100, ((down ?? 0) + (up ?? 0)) * 4),
      },
    ];
  });

  const sessionsList = createMemo(() => ws.state.rdsSessions ?? []);

  // Lanceur : filtre les outils par recherche, Entrée ouvre le 1er résultat.
  const launchMatches = createMemo<CockpitTool[]>(() => {
    const q = launchQuery().trim().toLowerCase();
    if (!q) return [];
    return props.availableTools.filter((t) => t.label.toLowerCase().includes(q)).slice(0, 8);
  });
  const recentChips = createMemo<CockpitTool[]>(() => props.availableTools.slice(0, 6));

  // Actions rapides → host:command best-effort (confirm sur destructif).
  function hostCmd(action: string, extra: Record<string, unknown> = {}) {
    const hostId = props.host?.hostId;
    if (!hostId) {
      notify.warn("Action indisponible — hôte inconnu", { durationMs: 3000 });
      return;
    }
    ws.send({ type: "host:command", hostId, command: { action, ...extra } } as never);
  }
  async function quickAction(a: { action: string; label: string; danger?: boolean }) {
    if (a.action === "screenshot") {
      props.onOpenControl();
      return;
    }
    if (a.danger) {
      const ok = await confirm.confirm({
        title: `${a.label} ?`,
        body: `Confirmer « ${a.label} » sur ${props.host?.displayName || props.host?.hostname || "l'hôte"} ?`,
        confirmLabel: a.label,
        level: "danger",
      });
      if (!ok) return;
    }
    hostCmd(a.action);
    notify.info(`${a.label} demandé`, { durationMs: 2500 });
  }
  const ACTIONS: { ic: string; action: string; label: string; danger?: boolean }[] = [
    { ic: "🔒", action: "lock", label: "Verrouiller" },
    { ic: "⟳", action: "restart", label: "Redémarrer", danger: true },
    { ic: "⎋", action: "logoff", label: "Fermer session", danger: true },
    { ic: "✉", action: "message", label: "Message" },
    { ic: "⬆", action: "elevate", label: "Élévation UAC" },
    { ic: "⎙", action: "screenshot", label: "Capture" },
  ];

  const h = () => props.host;
  const online = () => (props.sessionAgentOnline === undefined ? Boolean(h()?.online) : props.sessionAgentOnline);
  // Mode debug rapporté par le host (systemInfo.debugMode {active, untilMs}).
  const debugMode = (): { untilMs: number } | null => {
    const dm = sysStatic()["debugMode"] as { active?: boolean; untilMs?: number } | undefined;
    return dm?.active ? { untilMs: Number(dm.untilMs) || 0 } : null;
  };
  const debugLabel = (untilMs: number): string => {
    const msLeft = untilMs - Date.now();
    if (msLeft <= 0) return "DEBUG (expiré)";
    const s = Math.ceil(msLeft / 1000);
    return s < 90 ? `DEBUG (${s}s)` : `DEBUG (${Math.ceil(s / 60)}m)`;
  };
  function openDirect(mode: ControlLaunchMode) {
    props.onOpenControl(mode);
  }

  return (
    <div class="infoD">
      {/* ── Aperçu (gaté par consentement) ── */}
      <div class="cp-area-prev">
        <Show
          when={consent() === "granted"}
          fallback={
            <div class="card sk preview preview--locked" style={{ padding: "0" }}>
              <div class="pv-bar">
                <span class="live off">{consent() === "refused" ? "✕ REFUSÉ" : "◌ EN ATTENTE"}</span>
                <span class="soft">aucun flux vidéo transmis</span>
              </div>
              <div class="pv-body">
                <div class="lock-ic">{consent() === "refused" ? "⊘" : "🔒"}</div>
                <b>{consent() === "refused" ? "Aperçu indisponible" : "En attente du consentement"}</b>
                <small>
                  {consent() === "refused"
                    ? "L'utilisateur a refusé la prise en main — aucun aperçu n'est affiché."
                    : "Aucun aperçu tant que l'utilisateur n'a pas validé la demande sur son poste."}
                </small>
                <div class="remote-direct-actions">
                  <button class="reqbtn" onClick={() => openDirect("ws")} disabled={!online()}>
                    WS
                  </button>
                  <button class="reqbtn reqbtn--ghost" onClick={() => openDirect("rdp")} disabled={!online()}>
                    RDP
                  </button>
                </div>
                <button class="reqbtn reqbtn--wide" onClick={() => openDirect("ws")} disabled={!online()}>
                  {consent() === "refused" ? "Redemander la prise en main" : "Demander la prise en main"}
                </button>
              </div>
            </div>
          }
        >
          <div class="card sk preview" style={{ padding: "0" }}>
            <div class="pv-bar">
              <span class="live">
                <i />
                EN DIRECT
              </span>
              <span class="soft">prise en main accordée</span>
              <button class="pv-direct" onClick={() => openDirect("ws")} disabled={!online()}>
                WS
              </button>
              <button class="pv-direct" onClick={() => openDirect("rdp")} disabled={!online()}>
                RDP
              </button>
              <span class="pv-full" onClick={() => props.onOpenControl()}>
                ⤢ plein écran
              </span>
            </div>
            <div class="pv-body" onClick={() => props.onOpenControl()}>
              <div class="dash pv-fakewin">🖥</div>
              <b>Prise en main active</b>
              <small>clic = ouvrir le contrôle clavier / souris</small>
            </div>
          </div>
        </Show>
      </div>

      {/* ── Actions rapides ── */}
      <div class="cp-area-act">
        <div class="card sk">
          <h3>
            <span class="hi">⚡</span>Actions rapides
          </h3>
          <div class="acts">
            <For each={ACTIONS}>
              {(a) => (
                <div
                  class="actbtn sk2"
                  classList={{ danger: a.danger }}
                  onClick={() => void quickAction(a)}
                  title={a.label}
                >
                  <span class="ai">{a.ic}</span>
                  {a.label}
                </div>
              )}
            </For>
          </div>
        </div>
      </div>

      {/* ── Lanceur d'outils ── */}
      <div class="cp-area-lau">
        <div class="card sk launch">
          <h3>
            <span class="hi">▦</span>Lancer un outil
            <span class="more" onClick={() => props.onOpenSettings()}>
              {props.availableTools.length} dispo
            </span>
          </h3>
          <div class="lq sk2">
            <span class="mag">⌕</span>
            <input
              placeholder="Rechercher un outil — PowerShell, AD, Docker…"
              value={launchQuery()}
              onInput={(e) => setLaunchQuery(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && launchMatches()[0]) {
                  props.onOpenTool(launchMatches()[0].id);
                  setLaunchQuery("");
                }
              }}
            />
            <span class="kbd">↵</span>
          </div>
          <div class="chips">
            <For each={launchQuery().trim() ? launchMatches() : recentChips()}>
              {(t) => (
                <div
                  class="chip sk2"
                  onClick={() => {
                    props.onOpenTool(t.id);
                    setLaunchQuery("");
                  }}
                >
                  <span class="cti">{t.icon}</span>
                  {t.label}
                </div>
              )}
            </For>
          </div>
          <div class="launch-direct">
            <span class="more">Prise en main directe</span>
            <button class="chip sk2" onClick={() => openDirect("ws")} disabled={!online()}>
              <span class="cti">▶</span>
              WS
            </button>
            <button class="chip sk2" onClick={() => openDirect("rdp")} disabled={!online()}>
              <span class="cti">▣</span>
              RDP
            </button>
          </div>
        </div>
      </div>

      {/* ── Télémétrie (colonne droite) ── */}
      <div class="tele">
        {/* Identité */}
        <div class="card sk">
          <div class="id-top">
            <span class="dot" classList={{ off: !online() }} />
            <b>{h()?.displayName || h()?.hostname || "—"}</b>
            <span class="tag" classList={{ off: !online() }}>
              {online() ? "en ligne" : "hors ligne"}
            </span>
            <Show when={debugMode()}>
              {(d) => (
                <span class="tag debug" title="Agent en mode debug (relay interne loopback)">
                  {debugLabel(d().untilMs)}
                </span>
              )}
            </Show>
          </div>
          <div class="id-sub">{(h()?.clientLabel || "Sans client") + " › " + (h()?.siteLabel || "—")}</div>
          <dl class="id-grid">
            <dt>OS</dt>
            <dd>{str("osVersion", "osBuild") || h()?.platform || "—"}</dd>
            <dt>Adresse IP</dt>
            <dd>{str("privateIp", "primaryIp", "localIp", "ip") || "—"}</dd>
            <dt>Domaine</dt>
            <dd>{h()?.domainFqdn || "—"}</dd>
            <dt>Agent host</dt>
            <dd>{h()?.version || "—"}</dd>
          </dl>
        </div>

        {/* Santé */}
        <div class="card sk">
          <h3>
            <span class="hi">♥</span>État santé
            <span class="more" onClick={() => setShowDetails((v) => !v)}>
              {showDetails() ? "masquer" : "détails →"}
            </span>
          </h3>
          <div class="health">
            <For each={gauges()}>
              {(g) => (
                <div class="gauge">
                  <div class="g-top">
                    <b>{g.l}</b>
                    <span class="v">{g.v}</span>
                  </div>
                  <div class="g-bar">
                    <i style={{ width: `${Math.max(0, Math.min(100, g.p))}%` }} />
                  </div>
                </div>
              )}
            </For>
          </div>
        </div>

        {/* Sessions ouvertes */}
        <div class="card sk">
          <h3>
            <span class="hi">◉</span>Sessions ouvertes<span class="more">{sessionsList().length}</span>
            <Show when={props.onOpenAgentless}>
              <button class="ses-add" onClick={() => props.onOpenAgentless?.()} title="Connecter une autre machine">
                + Ouvrir autre
              </button>
            </Show>
          </h3>
          <Show
            when={sessionsList().length > 0}
            fallback={
              <div class="rail-empty" style={{ padding: "4px 0" }}>
                Aucune session énumérée.
              </div>
            }
          >
            <div class="ses">
              <For each={sessionsList()}>
                {(s) => (
                  <div class="ses-row sk2">
                    <span class="av" style={{ background: s.isConsole ? "var(--dot)" : "#d6a13a" }}>
                      {(s.userName || "?").slice(0, 1).toUpperCase()}
                    </span>
                    <span class="who">
                      <b>{s.userName || s.sessionName || `session ${s.sessionId}`}</b>
                      <small>
                        {s.isConsole ? "Console" : "RDP"} · {s.sessionName}
                      </small>
                    </span>
                    <Show when={s.locked !== undefined}>
                      <span
                        class="ses-lock"
                        classList={{ "ses-lock--on": s.locked === true }}
                        title={s.locked ? "Session verrouillée" : "Session déverrouillée"}
                      >
                        {s.locked ? "🔒 verr." : "🔓 déverr."}
                      </span>
                    </Show>
                    <span class="st" style={{ color: s.state === "Active" ? "var(--dot)" : "#d6a13a" }}>
                      {s.state}
                    </span>
                    <span class="ses-acts">
                      <button
                        class="pv-direct"
                        onClick={() => props.onOpenControl("ws")}
                        disabled={!online()}
                        title="Prise en main vidéo (WGC/H264)"
                      >
                        WS
                      </button>
                      <button
                        class="pv-direct"
                        onClick={() => startShadowRdp(s.sessionId)}
                        disabled={!online() || (shadowPending() && shadowTarget() === s.sessionId)}
                        title="Tunnel RDP (shadow si verrouillée)"
                      >
                        {shadowPending() && shadowTarget() === s.sessionId ? "…" : "RDP"}
                      </button>
                    </span>
                  </div>
                )}
              </For>
            </div>
          </Show>
        </div>
      </div>

      {/* Overlay RDP shadow (Lot F) — monté sur le port retourné par le host,
          repointé à chaud sur bascule verrouillage via rds:shadow_mode. */}
      <Show when={shadowOpen() && shadowPort() !== null}>
        <div class="ses-rdp-overlay" onClick={(e) => e.target === e.currentTarget && closeShadowRdp()}>
          <div class="ses-rdp-frame">
            <div class="ses-rdp-bar">
              <b>RDP</b>
              <span class="ses-rdp-mode">
                {shadowMode() === "shadow"
                  ? "shadow (session verrouillée)"
                  : shadowMode() === "classic"
                    ? "session classique"
                    : "tunnel"}
              </span>
              <span class="ses-rdp-port">127.0.0.1:{shadowPort()}</span>
              <button class="pv-direct" onClick={() => closeShadowRdp()}>
                Fermer
              </button>
            </div>
            <div class="ses-rdp-body">
              <ErrorBoundary
                fallback={(err) => (
                  <div class="panel-crash">
                    <strong>⚠ Tunnel RDP impossible.</strong>
                    <pre class="panel-crash__msg">{String(err?.message ?? err)}</pre>
                  </div>
                )}
              >
                <RdpPanel defaultHost="127.0.0.1" defaultPort={String(shadowPort())} />
              </ErrorBoundary>
            </div>
          </div>
        </div>
      </Show>

      {/* ── Détails complets (dépliable — réutilise InfoSection) ── */}
      <Show when={showDetails()}>
        <div class="cp-details" style={{ "grid-column": "1 / -1" }}>
          <InfoSection
            host={props.host}
            loading={props.loading}
            features={props.features}
            sessionAgentOnline={props.sessionAgentOnline}
          />
        </div>
      </Show>
    </div>
  );
}

// ─── Dialogue de connexion d'une machine ─────────────────────────────────
// Deux modes : « Code de session » (dépannage à la demande / client.html) et
// « Agentless RDP » (accès direct par IP, sans agent host).
function AgentlessDialog(props: {
  onClose: () => void;
  onConnect: (p: AgentlessParams) => void;
  onConnectByCode: (code: string) => void;
}) {
  const [mode, setMode] = createSignal<"code" | "rdp">("code");
  const [code, setCode] = createSignal("");
  const [host, setHost] = createSignal("");
  const [port, setPort] = createSignal("3389");
  const [user, setUser] = createSignal("");
  const [protocol, setProtocol] = createSignal("rdp");

  function submit() {
    if (mode() === "code") {
      if (!code().trim()) return;
      props.onConnectByCode(code().trim());
    } else {
      if (!host().trim()) return;
      props.onConnect({
        host: host().trim(),
        port: port().trim() || "3389",
        user: user().trim(),
        protocol: protocol(),
      });
    }
  }

  return (
    <div class="cp-dialog-backdrop" onClick={() => props.onClose()}>
      <div class="cp-dialog" onClick={(e) => e.stopPropagation()}>
        <h2>Connecter une machine</h2>

        <div class="cp-tabs">
          <button class="cp-tab" classList={{ "cp-tab--on": mode() === "code" }} onClick={() => setMode("code")}>
            Code de session
          </button>
          <button class="cp-tab" classList={{ "cp-tab--on": mode() === "rdp" }} onClick={() => setMode("rdp")}>
            Agentless (RDP)
          </button>
        </div>

        <Show when={mode() === "code"}>
          <p class="sub">
            Dépannage à la demande — saisissez le code d'assistance communiqué par le client (généré sur client.html),
            ou un code de session permanent.
          </p>
          <label>Code de session</label>
          <input
            class="cp-code"
            placeholder="ABC234XYZ"
            value={code()}
            onInput={(e) => setCode(e.currentTarget.value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase())}
            onKeyDown={(e) => e.key === "Enter" && submit()}
            autofocus
          />
        </Show>

        <Show when={mode() === "rdp"}>
          <p class="sub">Accès direct (RDP) à une machine sans agent RemoteWS installé.</p>
          <label>Hôte / IP</label>
          <input
            placeholder="10.0.0.5"
            value={host()}
            onInput={(e) => setHost(e.currentTarget.value)}
            onKeyDown={(e) => e.key === "Enter" && submit()}
          />
          <div class="cp-row">
            <div>
              <label>Port</label>
              <input value={port()} onInput={(e) => setPort(e.currentTarget.value)} />
            </div>
            <div>
              <label>Protocole</label>
              <select value={protocol()} onChange={(e) => setProtocol(e.currentTarget.value)}>
                <option value="rdp">RDP</option>
              </select>
            </div>
          </div>
          <label>Utilisateur (optionnel)</label>
          <input placeholder="DOMAINE\\utilisateur" value={user()} onInput={(e) => setUser(e.currentTarget.value)} />
        </Show>

        <div class="cp-actions">
          <button class="cp-btn" onClick={() => props.onClose()}>
            Annuler
          </button>
          <button class="cp-btn cp-btn--primary" onClick={submit}>
            Connecter
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── INFO — dashboard du host ────────────────────────────────────────────
function InfoSection(props: {
  host: HostData | null | undefined;
  loading: boolean;
  features: Record<string, boolean> | null;
  sessionAgentOnline?: boolean;
}) {
  const ws = useWs();
  const staticSysinfo = () => props.host?.systemInfo || {};
  const effectiveLiveData = () => {
    const live = liveData();
    if (Object.keys(live).length > 0) return live;
    const stat = staticSysinfo();
    if (Object.keys(stat).length === 0) return live;
    return {
      source: "static_fallback",
      cpuPercent: pickNum(stat, ["cpuLoadPercent", "cpuPercent", "cpu"]),
      memPercent: pickNum(stat, ["memoryLoadPercent", "ramPercent", "memoryPercent"]),
      privateIp: liveOrStatic("privateIp", "primaryIp", "localIp", "ip", "ipAddresses.0"),
      publicIp: liveOrStatic("publicIp", "externalIp", "wanIp"),
      osVersion: liveOrStatic("osVersion", "osBuild", "systemVersion"),
      arch: liveOrStatic("osArchitecture", "processArchitecture", "arch", "architecture"),
      memoryGb: formatGb(pickNum(stat, ["memoryTotalBytes", "memory.totalBytes"])),
    };
  };

  // ── Live sysinfo : envelope live/panel/subscribe, interval configurable ──
  const STORAGE_KEY = "rws.sysinfoIntervalMs";
  const initialInterval = (() => {
    try {
      const v = Number(localStorage.getItem(STORAGE_KEY));
      if (Number.isFinite(v) && v >= 2000) return v;
    } catch {
      /* noop */
    }
    return 5000;
  })();
  const [intervalMs, setIntervalMs] = createSignal(initialInterval);
  const [liveData, setLiveData] = createSignal<Record<string, unknown>>({});
  const [cpuHist, setCpuHist] = createSignal<number[]>([]);
  const [memHist, setMemHist] = createSignal<number[]>([]);
  const [lastSampleAt, setLastSampleAt] = createSignal<number | null>(null);
  const HIST_MAX = 60;

  onMount(() => {
    let subscribedAt = 0;
    const subTimer = setInterval(() => {
      if (!ws.isOpen() || !ws.state.sessionCode) return;
      // Re-subscribe si pas encore OU si l'interval a changé.
      if (subscribedAt !== intervalMs()) {
        try {
          ws.send(asWsOutgoing(envelope("live", "panel", "subscribe", { topic: "sysinfo", interval: intervalMs() })));
          subscribedAt = intervalMs();
          try {
            localStorage.setItem(STORAGE_KEY, String(intervalMs()));
          } catch {
            /* noop */
          }
        } catch {
          /* noop */
        }
      }
    }, 1000);

    const off = ws.on(
      (m) =>
        isWsEnvelope(m, "live", "panel", "data") &&
        (m as { payload?: { topic?: string } }).payload?.topic === "sysinfo",
      (msg) => {
        const data = (msg as { payload?: { data?: Record<string, unknown> } }).payload?.data || {};
        setLiveData(data);
        const cpu = pickNum(data, ["cpuPercent", "cpu", "cpuUsage", "cpu_percent"]);
        const mem = pickNum(data, ["memPercent", "memoryPercent", "mem", "memory_percent", "ramPercent"]);
        if (cpu !== null) setCpuHist((h) => [...h.slice(-(HIST_MAX - 1)), cpu]);
        if (mem !== null) setMemHist((h) => [...h.slice(-(HIST_MAX - 1)), mem]);
        setLastSampleAt(Date.now());
      },
    );

    onCleanup(() => {
      clearInterval(subTimer);
      if (subscribedAt && ws.isOpen()) {
        try {
          ws.send(asWsOutgoing(envelope("live", "panel", "unsubscribe", { topic: "sysinfo" })));
        } catch {
          /* noop */
        }
      }
      off();
    });
  });

  // Helpers de lecture : live d'abord, puis fallback sur system_info statique.
  function getByPath(data: unknown, path: string): unknown {
    if (!path) return undefined;
    const parts = path.split(".");
    let cur: unknown = data;
    for (const p of parts) {
      if (cur === null || cur === undefined || typeof cur !== "object") return undefined;
      cur = (cur as Record<string, unknown>)[p];
    }
    return cur;
  }
  function liveStr(k: string): string | null {
    const v = getByPath(liveData(), k);
    if (v === undefined || v === null || v === "") return null;
    return String(v);
  }
  function staticStr(k: string): string | null {
    const v = getByPath(staticSysinfo(), k);
    if (v === undefined || v === null) return null;
    return String(v);
  }
  function liveOrStatic(...keys: string[]): string {
    for (const k of keys) {
      const v = liveStr(k);
      if (v !== null) return v;
    }
    for (const k of keys) {
      const v = staticStr(k);
      if (v !== null) return v;
    }
    return "—";
  }
  function formatGb(bytes: number | null): string {
    if (bytes === null || !Number.isFinite(bytes) || bytes <= 0) return "—";
    return `${Math.round((bytes / 1024 / 1024 / 1024) * 10) / 10}`;
  }
  function liveOr(...keys: string[]): string {
    for (const k of keys) {
      const v = liveStr(k);
      if (v !== null) return v;
    }
    for (const k of keys) {
      const v = staticStr(k);
      if (v !== null) return v;
    }
    return "—";
  }
  function memoryGbValue(): string {
    const direct = liveOr(
      "memoryGb",
      "memoryGB",
      "totalMemoryGb",
      "ramGb",
      "ram.gb",
      "system.memoryGb",
      "systemInfo.memoryGb",
    );
    return direct !== "—" ? direct : formatGb(pickNum(staticSysinfo(), ["memoryTotalBytes", "memory.totalBytes"]));
  }

  const intervalLabel = () => (intervalMs() >= 60000 ? `${intervalMs() / 60000} min` : `${intervalMs() / 1000} s`);
  const intervalSelectStyle = {
    background: "var(--bg-2, #0f172a)",
    color: "var(--text-1, #e8ecf3)",
    border: "1px solid var(--border, #2a3140)",
    "border-radius": "4px",
    padding: "4px 8px",
    "font-size": "12px",
  } as const;

  return (
    <div class="info-section">
      <Show when={props.loading}>
        <div class="info-loading">Chargement des informations du host…</div>
      </Show>
      <Show when={!props.loading && !props.host}>
        <div class="info-empty">Pas de host trouvé pour cette session.</div>
      </Show>
      <Show when={props.host}>
        {(h) => (
          <div class="info-grid">
            <Card title="Système">
              <Row k="Hostname" v={h().hostname} />
              <Row k="Nom affiché" v={h().displayName || "—"} />
              <Row k="Plateforme" v={h().platform} />
              <Row k="Domaine" v={h().domainFqdn || "—"} />
              <Row k="Agent" v={h().version} />
              <Row
                k="État"
                v={
                  props.sessionAgentOnline === undefined
                    ? h().online
                      ? "En ligne ✓"
                      : "Hors ligne ✗"
                    : props.sessionAgentOnline
                      ? "En ligne ✓"
                      : "Hors ligne ✗"
                }
              />
              <Row k="Dernière activité" v={new Date(h().lastSeen).toLocaleString()} />
              <Row k="Premier enrôlement" v={new Date(h().firstSeen).toLocaleString()} />
            </Card>

            <Card title="Affectation">
              <Row k="Tenant" v={h().tenantId ? h().tenantId!.slice(0, 8) + "…" : "Plateforme (aucun)"} />
              <Row k="Client" v={h().clientLabel || "Sans client"} />
              <Row k="Site" v={h().siteLabel || "—"} />
              <Row k="Niveau de risque" v={h().riskLevel || "—"} />
              <Row k="Tags" v={h().tags.length ? h().tags.join(", ") : "—"} />
            </Card>

            <Card title="Hardware / OS">
              <Row
                k="CPU"
                v={liveOr("cpu", "processor", "cpuModel", "system.cpu", "systemInfo.cpu", "os.cpu", "processorModel")}
              />
              <Row
                k="Cœurs"
                v={liveOr("cpuCores", "cpus", "processorCount", "cores", "system.cpuCores", "systemInfo.cpuCores")}
              />
              <Row k="RAM (Go)" v={memoryGbValue()} />
              <Row
                k="Version OS"
                v={liveOr("osVersion", "osBuild", "system.version", "systemInfo.osVersion", "os.version")}
              />
              <Row
                k="Architecture"
                v={liveOr(
                  "arch",
                  "architecture",
                  "osArchitecture",
                  "processArchitecture",
                  "system.arch",
                  "systemInfo.arch",
                )}
              />
            </Card>

            <Card title="Réseau (live)">
              <Row
                k="État"
                v={
                  props.sessionAgentOnline === undefined
                    ? liveOr("networkState", "netState") !== "—"
                      ? liveOr("networkState", "netState")
                      : h().online
                        ? "Connecté"
                        : "Hors ligne"
                    : props.sessionAgentOnline
                      ? "Connecté"
                      : "Hors ligne"
                }
              />
              <Row k="Type" v={liveOr("connectionType", "netType", "interfaceType", "networkType", "linkType")} />
              <Row k="SSID Wi-Fi" v={liveOr("wifiSsid", "ssid")} />
              <Row
                k="IP privée"
                v={liveOr(
                  "privateIp",
                  "primaryIp",
                  "localIp",
                  "ip",
                  "ipAddresses.0",
                  "privateIpV4",
                  "localAddress",
                  "localIpV4",
                  "ipv4",
                )}
              />
              <Row k="Passerelle" v={liveOr("gateway", "defaultGateway")} />
              <Row k="DNS primaire" v={liveOr("dns", "dnsServers", "dns1", "dnsPrimary")} />
              <Row k="DNS secondaire" v={liveOr("dns2", "dnsSecondary")} />
              <Row k="MAC" v={liveOr("mac", "macAddress")} />
              <Row k="IP publique" v={liveOr("publicIp", "externalIp")} />
              <Row k="VPN actif" v={liveOr("vpnActive", "vpn")} />
              <Row k="IP VPN" v={liveOr("vpnIp")} />
              <Row k="Adaptateur VPN" v={liveOr("vpnAdapter", "vpnName")} />
            </Card>

            <Card title={`Live monitoring (${intervalLabel()})`} wide>
              <div style={{ padding: "10px" }}>
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    "align-items": "center",
                    "margin-bottom": "10px",
                    "font-size": "12px",
                    color: "var(--text-3)",
                  }}
                >
                  <span>Fréquence de polling :</span>
                  <select
                    style={intervalSelectStyle}
                    value={String(intervalMs())}
                    onChange={(e) => setIntervalMs(Number(e.currentTarget.value))}
                    title="Plus le polling est rapide, plus l'agent consomme du CPU."
                  >
                    <option value="2000">2 s (charge max)</option>
                    <option value="5000">5 s (défaut)</option>
                    <option value="10000">10 s</option>
                    <option value="30000">30 s</option>
                    <option value="60000">1 min</option>
                  </select>
                  <span style={{ "font-size": "10px", color: "var(--text-4)" }}>
                    (interval bas = charge CPU agent +)
                  </span>
                </div>
                <LiveMonitoring
                  cpuHist={cpuHist()}
                  memHist={memHist()}
                  agentCpu={pickNum(liveData(), ["agentCpuPercent", "agentCpu", "agent_cpu_percent"])}
                  agentMem={pickNum(liveData(), ["agentMemoryMb", "agentMemMb", "agent_memory_mb", "agentRssMb"])}
                  lastSampleAt={lastSampleAt()}
                />
              </div>
            </Card>

            <Card title="Capacités agent">
              <div style={{ display: "flex", "flex-wrap": "wrap", gap: "6px", padding: "8px" }}>
                <Show
                  when={props.features && Object.keys(props.features).length > 0}
                  fallback={
                    <span style={{ color: "var(--text-4)", "font-size": "12px" }}>Aucune capacité reportée.</span>
                  }
                >
                  <For each={Object.entries(props.features || {})}>
                    {([k, v]) => (
                      <span class="chip" data-on={v ? "true" : "false"} style={{ "font-size": "11px" }}>
                        {v ? "✓" : "○"} {k}
                      </span>
                    )}
                  </For>
                </Show>
              </div>
            </Card>

            <Show when={Object.keys(effectiveLiveData()).length > 0 || Object.keys(staticSysinfo()).length > 0}>
              <Card title="Données brutes (live + static)" wide>
                <pre
                  style={{
                    margin: "0",
                    padding: "10px",
                    "font-family": "var(--font-mono)",
                    "font-size": "11px",
                    color: "var(--text-3)",
                    "max-height": "260px",
                    "overflow-y": "auto",
                    "white-space": "pre-wrap",
                    "word-break": "break-word",
                  }}
                >
                  {JSON.stringify({ live: effectiveLiveData(), static: staticSysinfo() }, null, 2)}
                </pre>
              </Card>
            </Show>
          </div>
        )}
      </Show>
    </div>
  );
}

function Card(props: { title: string; wide?: boolean; children: unknown }) {
  return (
    <div
      class="info-card"
      style={{
        background: "var(--surface-2, #1a1f2c)",
        border: "1px solid var(--border, #2a3140)",
        "border-radius": "6px",
        "grid-column": props.wide ? "1 / -1" : undefined,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "8px 12px",
          background: "var(--surface-3, #232a3a)",
          "border-bottom": "1px solid var(--border, #2a3140)",
          "font-size": "11px",
          "font-weight": "600",
          "text-transform": "uppercase",
          "letter-spacing": "0.05em",
          color: "var(--text-3, #94a3b8)",
        }}
      >
        {props.title}
      </div>
      <dl style={{ margin: "0", padding: "0", display: "grid", "grid-template-columns": "auto 1fr", gap: "0" }}>
        {props.children as never}
      </dl>
    </div>
  );
}

function Row(props: { k: string; v: string }) {
  return (
    <>
      <dt
        style={{
          padding: "6px 12px",
          "font-size": "11px",
          color: "var(--text-4, #98a2b6)",
          "border-bottom": "1px solid var(--border, #2a3140)",
        }}
      >
        {props.k}
      </dt>
      <dd
        style={{
          margin: "0",
          padding: "6px 12px",
          "font-size": "12px",
          color: "var(--text-1, #e8ecf3)",
          "border-bottom": "1px solid var(--border, #2a3140)",
          "word-break": "break-word",
        }}
      >
        {props.v}
      </dd>
    </>
  );
}

// ─── LIVE MONITORING — composant pur (présentationnel) ───────────────────
// Reçoit l'historique CPU/RAM et les valeurs courantes en props. La
// subscription live/panel/subscribe est gérée par InfoSection (parent) pour
// permettre aux autres cards (Réseau, Hardware) de lire la même donnée.
function LiveMonitoring(props: {
  cpuHist: number[];
  memHist: number[];
  agentCpu: number | null;
  agentMem: number | null;
  lastSampleAt: number | null;
}) {
  const lastValue = (arr: number[]) => (arr.length ? arr[arr.length - 1] : null);
  const hasData = () => props.cpuHist.length > 0 || props.memHist.length > 0;

  return (
    <div style={{ display: "grid", "grid-template-columns": "repeat(auto-fit, minmax(280px, 1fr))", gap: "14px" }}>
      <Metric label="CPU système" unit="%" value={lastValue(props.cpuHist)} history={props.cpuHist} color="#38bdf8" />
      <Metric
        label="Mémoire système"
        unit="%"
        value={lastValue(props.memHist)}
        history={props.memHist}
        color="#a78bfa"
      />
      <Metric label="CPU agent" unit="%" value={props.agentCpu} history={[]} color="#34d399" />
      <Metric label="Mémoire agent" unit="Mo" value={props.agentMem} history={[]} color="#fbbf24" />
      <div style={{ "grid-column": "1 / -1", "font-size": "11px", color: "var(--text-4)" }}>
        <Show
          when={hasData()}
          fallback={
            <span>
              En attente du topic <code>sysinfo</code> côté agent (le tool builtin doit publier
              <code> live/panel/data topic="sysinfo"</code>).
            </span>
          }
        >
          <span>
            {props.cpuHist.length} échantillons · dernier{" "}
            {props.lastSampleAt ? `il y a ${Math.round((Date.now() - props.lastSampleAt) / 1000)}s` : "—"}
          </span>
        </Show>
      </div>
    </div>
  );
}

function pickNum(obj: Record<string, unknown>, keys: string[]): number | null {
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === "number" && Number.isFinite(v)) return v;
  }
  return null;
}

function Metric(props: { label: string; unit: string; value: number | null; history: number[]; color: string }) {
  const W = 240;
  const H = 50;
  const path = () => {
    const vals = props.history;
    if (vals.length < 2) return "";
    const max = Math.max(100, ...vals);
    const dx = W / (vals.length - 1);
    return vals
      .map((v, i) => `${i === 0 ? "M" : "L"}${(i * dx).toFixed(1)},${(H - (v / max) * H).toFixed(1)}`)
      .join(" ");
  };
  return (
    <div style={{ background: "var(--surface-3, #232a3a)", "border-radius": "4px", padding: "10px" }}>
      <div
        style={{
          display: "flex",
          "justify-content": "space-between",
          "align-items": "baseline",
          "margin-bottom": "4px",
        }}
      >
        <span
          style={{
            "font-size": "11px",
            color: "var(--text-3, #94a3b8)",
            "text-transform": "uppercase",
            "letter-spacing": "0.05em",
          }}
        >
          {props.label}
        </span>
        <span style={{ "font-size": "16px", "font-weight": "600", color: "var(--text-1, #e8ecf3)" }}>
          {props.value !== null ? `${props.value.toFixed(1)} ${props.unit}` : "—"}
        </span>
      </div>
      <Show when={props.history.length >= 2}>
        <svg
          width={W}
          height={H}
          style={{ display: "block", width: "100%", height: `${H}px` }}
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="none"
        >
          <path d={path()} fill="none" stroke={props.color} stroke-width="1.5" />
        </svg>
      </Show>
      <Show when={props.history.length < 2 && props.value !== null}>
        <div style={{ "font-size": "11px", color: "var(--text-4, #98a2b6)" }}>
          Valeur ponctuelle (pas d'historique).
        </div>
      </Show>
    </div>
  );
}

// ─── PLUGINS — sous-onglets sur les outils agent (Terminal, Fichiers…) ───
// Persistence : tous les sous-tabs visités restent montés.
function _PluginsSection() {
  const ws = useWs();
  const features = () => ws.state.agentFeatures || null;
  const pluginVersions = () => ws.state.pluginVersions || {};

  // Tab dynamique : 1 onglet par panel activé sur l'agent (core + feature-gated
  // dont la capacité est reportée). L'ordre suit PANEL_REGISTRY.
  const availableEntries = createMemo<PanelEntry[]>(() =>
    PANEL_REGISTRY.filter((p) => {
      if (!p.component) return false;
      const pluginAvailable = panelPluginAvailable(p, pluginVersions());
      const featureAvailable = !p.featureGate || features()?.[p.featureGate] === true;
      return featureAvailable || pluginAvailable;
    }),
  );

  // Init : 1er entry dispo (typiquement powershell).
  const initialId = PANEL_REGISTRY.find((p) => !p.featureGate && p.component)?.id || "";
  const [activeTab, setActiveTab] = createSignal<string>(initialId);
  const [visitedTabs, setVisitedTabs] = createSignal<Set<string>>(new Set(initialId ? [initialId] : []));

  function activateTab(id: string) {
    setVisitedTabs((v) => {
      if (v.has(id)) return v;
      const n = new Set(v);
      n.add(id);
      return n;
    });
    setActiveTab(id);
  }

  const labelOf = (entry: PanelEntry) => PLUGIN_LABELS[entry.id] || entry.title || entry.id;

  return (
    <div
      class="plugins-section"
      style={{ display: "flex", "flex-direction": "column", flex: "1 1 0", "min-height": "0" }}
    >
      <nav
        class="plugin-subtabs"
        style={{
          display: "flex",
          gap: "2px",
          padding: "0 10px",
          "border-bottom": "1px solid var(--border, #2a3140)",
          "overflow-x": "auto",
          "-webkit-overflow-scrolling": "touch",
          background: "var(--surface-1, #0f172a)",
          "flex-shrink": "0",
        }}
      >
        <Show
          when={availableEntries().length > 0}
          fallback={
            <div style={{ padding: "12px", color: "var(--text-4)", "font-size": "12px" }}>
              Aucun plugin activé sur cet agent.
            </div>
          }
        >
          <For each={availableEntries()}>
            {(entry) => (
              <button
                type="button"
                onClick={() => activateTab(entry.id)}
                style={{
                  background: activeTab() === entry.id ? "var(--surface-2, #1a1f2c)" : "transparent",
                  color: activeTab() === entry.id ? "var(--text-1)" : "var(--text-3)",
                  border: "none",
                  "border-bottom":
                    activeTab() === entry.id ? "2px solid var(--accent, #38bdf8)" : "2px solid transparent",
                  padding: "10px 14px",
                  cursor: "pointer",
                  "font-size": "13px",
                  "font-weight": "500",
                  "white-space": "nowrap",
                  "min-height": "44px",
                }}
                title={entry.featureGate ? `Plugin : ${entry.featureGate}` : "Core"}
              >
                {labelOf(entry)}
              </button>
            )}
          </For>
        </Show>
      </nav>

      <div class="plugin-content" style={{ flex: "1 1 0", "min-height": "0", "overflow-y": "auto" }}>
        <For each={availableEntries()}>
          {(entry) => (
            <Show when={visitedTabs().has(entry.id) && entry.component}>
              <div style={{ display: activeTab() === entry.id ? "block" : "none", height: "100%" }}>
                <Dynamic component={entry.component as Component} />
              </div>
            </Show>
          )}
        </For>
      </div>
    </div>
  );
}
